import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { MapPin, Calendar, Clock, User, Phone, Mail, Car, Search, Filter, Eye, Edit, Trash2, PoundSterling, Settings, Tag, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBookings, setTotalBookings] = useState(0);
  const [bookingStats, setBookingStats] = useState({total_revenue: 0, completed: 0, pending: 0, in_progress: 0});
  const [filteredBookings, setFilteredBookings] = useState([]);

  // Check if admin is logged in
  useEffect(() => {
    const isLoggedIn = localStorage.getItem("adminLoggedIn");
    if (!isLoggedIn) {
      navigate("/admin/login");
    }
  }, [navigate]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedBookingIds, setSelectedBookingIds] = useState([]);
  const [bookingFromDate, setBookingFromDate] = useState("");
  const [bookingToDate, setBookingToDate] = useState("");
  const [bookingSortOrder, setBookingSortOrder] = useState("desc");
  const [selectedDriver, setSelectedDriver] = useState("");
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  
  // Add Driver Dialog states
  const [addDriverDialogOpen, setAddDriverDialogOpen] = useState(false);
  const [newDriverData, setNewDriverData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    address: "",
    vehicle_type: "saloon",
    license_plate: "",
    driver_type: "dsa",
    driver_category: "",
    // Licenses
    dvla_licence: "",
    dvla_licence_expiry: "",
    phd_licence: "",
    phd_licence_expiry: "",
    phv_licence: "",
    phv_licence_expiry: "",
    insurance_certificate: "",
    insurance_certificate_expiry: "",
    // Additional info
    ni_number: "",
    bank_name: "",
    bank_account_number: "",
    bank_sort_code: "",
    car_make: "",
    car_model: "",
    car_colour: "",
    max_seat: 4
  });
  
  // Edit Driver Dialog states
  const [editDriverDialogOpen, setEditDriverDialogOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [viewingDriver, setViewingDriver] = useState(null);
  const [viewDriverDialogOpen, setViewDriverDialogOpen] = useState(false);

  // Driver Categories
  const [driverCategories, setDriverCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [driverCategoryFilter, setDriverCategoryFilter] = useState("");

  const getErrorMsg = (error, fallback) => {
    const detail = error.response?.data?.detail;
    if (Array.isArray(detail)) return detail.map(d => d.msg || String(d)).join(", ");
    if (typeof detail === "string") return detail;
    return fallback;
  };
  
  // Delete Confirmation Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState(null);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState("all");
  const [vehicleFilter, setVehicleFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Pricing Management states
  const [mileRanges, setMileRanges] = useState([
    { id: '1', from_miles: 0, to_miles: 8, price_type: 'fixed', saloon: 30, estate: 35, luxury: 50, mpv: 40, executive: 55 },
    { id: '2', from_miles: 8, to_miles: 15, price_type: 'per_mile', saloon: 2.5, estate: 2.8, luxury: 3.5, mpv: 3.0, executive: 4.0 },
    { id: '3', from_miles: 15, to_miles: 25, price_type: 'per_mile', saloon: 2.3, estate: 2.5, luxury: 3.2, mpv: 2.8, executive: 3.5 }
  ]);
  const [pricingConfig, setPricingConfig] = useState({ base_pricing: {}, zipcode_pricing: {} });
  const [editPricingDialogOpen, setEditPricingDialogOpen] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  const [addZipcodeDialogOpen, setAddZipcodeDialogOpen] = useState(false);
  const [newZipcodePricing, setNewZipcodePricing] = useState({
    zipcode: "",
    vehicle_type: "saloon",
    base_fare_10_miles: 37.0,
    per_mile_after_10: 4.0
  });

  // Discount Code Management states
  const [discountCodes, setDiscountCodes] = useState([]);
  const [addDiscountDialogOpen, setAddDiscountDialogOpen] = useState(false);
  const [editDiscountDialogOpen, setEditDiscountDialogOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [newDiscount, setNewDiscount] = useState({
    code: "",
    type: "percentage",
    value: 0,
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    max_uses_per_user: 1,
    is_active: true
  });

  // Trip-based Discount states
  const [tripDiscounts, setTripDiscounts] = useState({
    one_way: { type: "amount", value: 5.0, enabled: true },
    round_trip: { type: "amount", value: 10.0, enabled: true }
  });

  // Invoice states
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState({});
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);

  // Contract states
  const [contracts, setContracts] = useState([]);
  const [addContractDialogOpen, setAddContractDialogOpen] = useState(false);
  const [manageContractsDialogOpen, setManageContractsDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [editContractDialogOpen, setEditContractDialogOpen] = useState(false);
  const [viewContractDialogOpen, setViewContractDialogOpen] = useState(false);
  const [viewingContract, setViewingContract] = useState(null);
  const [contractSearchQuery, setContractSearchQuery] = useState("");
  const [manageCustomersDialogOpen, setManageCustomersDialogOpen] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState("");
  const [addDropdownValue, setAddDropdownValue] = useState("");
  const [reportsDropdownValue, setReportsDropdownValue] = useState("");
  const [newContract, setNewContract] = useState({
    contract_title: "",
    contract_number: "",
    customer_id: "",
    contract_type: "monthly",
    start_date: "",
    end_date: "",
    monthly_rides: "",
    rate_per_ride: "",
    total_amount: "",
    // Ride details for auto-fill
    service_type: "airport_transfer",
    ride_type: "one_way",
    car_type: "saloon",
    max_passengers: "4",
    total_miles: "",
    total_fare: "",
    pickup_location: "",
    drop_location: "",
    pickup_time: "",
    drop_time: ""
  });

  // Customer states
  const [customers, setCustomers] = useState([]);
  const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);
  const [editCustomerDialogOpen, setEditCustomerDialogOpen] = useState(false);
  const [viewCustomerDialogOpen, setViewCustomerDialogOpen] = useState(false);
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    email: "",
    phone: "",
    address: ""
  });

  // Bulk Ride states
  const [addBulkRideDialogOpen, setAddBulkRideDialogOpen] = useState(false);
  const [bulkRideData, setBulkRideData] = useState({
    contract_id: "",
    entry_from_date: "",
    entry_till_date: "",
    driver_id: "",
    customer_id: "",
    customer_name: "",
    ride_type: "one_way",
    service_type: "",
    car_type: "",
    max_passengers: "",
    total_miles: "",
    total_fare: "",
    pickup_location: "",
    dropoff_location: "",
    pickup_time: "",
    dropoff_time: "",
    payment_mode: "contract_billing"
  });

  // Add Booking Dialog states
  const [addBookingDialogOpen, setAddBookingDialogOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(true);
  const [bookingListDialogOpen, setBookingListDialogOpen] = useState(false);
  const [allocateDriverDialogOpen, setAllocateDriverDialogOpen] = useState(false);
  const [selectedBookingForDriver, setSelectedBookingForDriver] = useState(null);
  const [newBookingData, setNewBookingData] = useState({
    contract_id: "",
    customer_id: "",
    customer_name: "",
    supplier_name: "",
    driver_id: "",
    ride_type: "one_way",
    service_type: "airport_transport",
    total_miles: "",
    total_fare: "",
    pickup_location: "",
    drop_location: "",
    pickup_date: "",
    dropoff_date: "",
    pickup_time: "",
    dropoff_time: "",
    car_type: "",
    max_passengers: "",
    payment_mode: "cash",
    contract_type: "organisation"
  });

  // Report states
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);

  const [siteSettings, setSiteSettings] = useState({ logo_url: "", logo_text: "Roman Cars", tagline: "", contact: { phone: "", email: "", address: "" } });
  
  // Driver Report Dialog
  const [driverReportDialogOpen, setDriverReportDialogOpen] = useState(false);
  const [driverReportData, setDriverReportData] = useState({
    from_date: "",
    to_date: "",
    driver_id: "",
    driver_category: "",
    select_all_drivers: true,
    supplier: ""
  });
  const [driverReportResults, setDriverReportResults] = useState(null);
  
  // Contract Report Dialog
  const [contractReportDialogOpen, setContractReportDialogOpen] = useState(false);
  const [contractReportData, setContractReportData] = useState({
    contract_id: "",
    from_date: "",
    to_date: "",
    invoice_note: "",
    invoice_no: "",
    invoice_date: ""
  });
  const [contractInvoiceDialogOpen, setContractInvoiceDialogOpen] = useState(false);
  const [contractInvoiceData, setContractInvoiceData] = useState(null);
  const [rideReportDialogOpen, setRideReportDialogOpen] = useState(false);
  const [rideReportData, setRideReportData] = useState({ start_date: "", end_date: "", search_query: "" });
  const [rideReportResults, setRideReportResults] = useState([]);

  const printContractInvoice = () => {
    if (!contractInvoiceData) return;
    
    const rides = (contractInvoiceData.rides || []).sort((a, b) => {
      const dateA = new Date(a.pickup_date || a.booking_date || '1970-01-01');
      const dateB = new Date(b.pickup_date || b.booking_date || '1970-01-01');
      return dateA - dateB;
    });
    const total = rides.reduce((sum, r) => sum + (r.price || 0), 0).toFixed(2);
    
    const ridesHTML = rides.length > 0 
      ? rides.map(ride => 
          '<tr>' +
          '<td style="border:1px solid #666;padding:8px;">' + new Date(ride.pickup_date || ride.booking_date || ride.created_at).toLocaleDateString('en-GB') + '</td>' +
          '<td style="border:1px solid #666;padding:8px;">' + (ride.booking_time || ride.pickup_time || '00:00') + ' To ' + (ride.dropoff_time || '00:00') + '</td>' +
          '<td style="border:1px solid #666;padding:8px;">' + (ride.pickup_address || ride.pickup_location?.address || 'N/A') + '</td>' +
          '<td style="border:1px solid #666;padding:8px;">' + (ride.dropoff_address || ride.dropoff_location?.address || 'N/A') + '</td>' +
          '<td style="border:1px solid #666;padding:8px;text-align:right;">£ ' + (ride.price || 0).toFixed(2) + '</td>' +
          '<td style="border:1px solid #666;padding:8px;text-align:right;">£ ' + (ride.price || 0).toFixed(2) + '</td>' +
          '</tr>'
        ).join('')
      : '<tr><td colspan="6" style="border:1px solid #666;padding:20px;text-align:center;">No rides found</td></tr>';
    
    const html = '<!DOCTYPE html><html><head><title>Invoice ' + contractInvoiceData.invoice_no + '</title></head><body style="font-family:Arial,sans-serif;padding:20px;max-width:900px;margin:0 auto;">' +
      '<div style="display:flex;justify-content:space-between;align-items:center;padding:15px;border-bottom:2px solid #000;margin-bottom:20px;">' +
        '<img src="/brand-logo.png" style="height:80px;" />' +
        '<h2 style="font-size:28px;margin:0;">Booking Invoice</h2>' +
      '</div>' +
      '<div style="background:#FDE047;padding:12px 16px;margin-bottom:15px;display:flex;justify-content:space-between;">' +
        '<span><strong>Customer Name :</strong> ' + (contractInvoiceData.customer?.name || contractInvoiceData.contract?.contract_title || 'N/A') + '</span>' +
        '<span><strong>Customer reference number :</strong> ' + (contractInvoiceData.contract?.contract_number || 'N/A') + '</span>' +
      '</div>' +
      '<div style="display:flex;margin-bottom:15px;">' +
        '<div style="flex:1;border-right:1px solid #ccc;padding-right:20px;">' +
          '<p><strong>Supplier Name :</strong> Roman cars St Albans</p>' +
          '<p><strong>License number :</strong> LN202300</p>' +
        '</div>' +
        '<div style="flex:1;padding-left:20px;">' +
          '<p><strong>End of contract :</strong> ' + (contractInvoiceData.customer?.name || 'N/A') + '</p>' +
          '<p><strong>Invoice No :</strong> ' + contractInvoiceData.invoice_no + '</p>' +
        '</div>' +
      '</div>' +
      '<div style="background:#FDE047;padding:12px 16px;margin-bottom:15px;">' +
        '<strong>Invoice date :</strong> ' + new Date(contractInvoiceData.invoice_date).toLocaleDateString('en-GB') +
      '</div>' +
      '<div style="display:flex;margin-bottom:15px;">' +
        '<div style="flex:1;border-right:1px solid #ccc;padding-right:20px;">' +
          '<p><strong>Metro Bank Sort code :</strong> 230580</p>' +
          '<p><strong>AC number :</strong> 31961475</p>' +
        '</div>' +
        '<div style="flex:1;padding-left:20px;">' +
          '<p><strong>Email :</strong> ' + (contractInvoiceData.customer?.email || 'N/A') + '</p>' +
          '<p><strong>Address :</strong> ' + (contractInvoiceData.customer?.address || 'N/A') + '</p>' +
        '</div>' +
      '</div>' +
      '<div style="background:#FDE047;padding:10px 16px;margin-bottom:10px;"><strong>Details</strong></div>' +
      '<table style="width:100%;border-collapse:collapse;margin-bottom:15px;">' +
        '<thead style="background:#E5E7EB;">' +
          '<tr>' +
            '<th style="border:1px solid #666;padding:8px;text-align:left;">Date</th>' +
            '<th style="border:1px solid #666;padding:8px;text-align:left;">Time</th>' +
            '<th style="border:1px solid #666;padding:8px;text-align:left;">Pickup</th>' +
            '<th style="border:1px solid #666;padding:8px;text-align:left;">Drop off</th>' +
            '<th style="border:1px solid #666;padding:8px;text-align:right;">Total Fare</th>' +
            '<th style="border:1px solid #666;padding:8px;text-align:right;">Subtotal</th>' +
          '</tr>' +
        '</thead>' +
        '<tbody>' + ridesHTML + '</tbody>' +
      '</table>' +
      '<div style="background:#FDE047;padding:15px 16px;margin-bottom:15px;text-align:right;">' +
        '<strong style="font-size:20px;">Total Payable : £ ' + total + '</strong>' +
      '</div>' +
      '<div style="margin-bottom:20px;">' +
        '<p><strong>NOTE:</strong> ' + (contractInvoiceData.invoice_note || 'N/A') + '</p>' +
      '</div>' +
    '</body></html>';
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 500);
  };


  useEffect(() => {
    fetchBookings();
    fetchDrivers();
    fetchPricingConfig();
    fetchDiscountCodes();
    fetchTripDiscounts();
    fetchContracts();
    fetchCustomers();
    fetchDriverCategories();
    fetchSiteSettings();
  }, []);

  useEffect(() => {
    filterBookings();
  }, [bookings, statusFilter, vehicleFilter, searchQuery]);
  
  useEffect(() => {
    fetchBookings(1);
  }, [searchQuery]);

  const fetchBookings = async (page = currentPage) => {
    try {
      const response = await axios.get(`${API}/admin/bookings?page=${page}&per_page=50&search=${searchQuery}`);
      setBookings(response.data.bookings || []);
      setTotalPages(response.data.total_pages || 1);
      setTotalBookings(response.data.total || 0);
      setCurrentPage(page);
      if (response.data.stats) {
        setBookingStats(response.data.stats);
      }
    } catch (error) {
      toast.error("Failed to fetch bookings");
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await axios.get(`${API}/admin/drivers`);
      setDrivers(response.data.drivers);
    } catch (error) {
      toast.error("Failed to fetch drivers");
    }
  };

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/cms/site-settings`);
      if (response.data?.settings) setSiteSettings(response.data.settings);
    } catch (e) { console.error('Failed to fetch site settings'); }
  };

    const printInvoice = () => {
    let node = document.getElementById('invoice-print');
    if (!node) {
      const dialogs = document.querySelectorAll('[role="dialog"], [data-radix-popper-content-wrapper], .fixed');
      for (let i = dialogs.length-1; i >= 0; i--) {
        const d = dialogs[i];
        const r = d.getBoundingClientRect();
        if (r.width > 200 && r.height > 200) { node = d; break; }
      }
    }
    if (!node) { alert('Nothing to print'); return; }
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) { alert('Popup blocked. Please allow popups.'); return; }
    w.document.write('<html><head><title>Invoice</title>');
    w.document.write('<style>body{font-family:Arial,sans-serif;padding:20px;color:#000;background:#fff;} table{width:100%;border-collapse:collapse;margin:8px 0;} th,td{border:1px solid #999;padding:6px;font-size:12px;text-align:left;} .bg-yellow-300{background:#fde047 !important;} .text-right{text-align:right;} img{max-height:80px;} h1,h2,h3{margin:6px 0;} p{margin:3px 0;font-size:13px;} .no-print{display:none;}</style>');
    w.document.write('</head><body>' + node.innerHTML + '</body></html>');
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 500);
  };

  const fetchDriverCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/driver-categories`);
      setDriverCategories(response.data.categories || []);
    } catch (error) {
      console.error("Failed to fetch categories");
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await axios.post(`${API}/admin/driver-categories`, { name: newCategoryName.trim() });
      toast.success("Category added!");
      setNewCategoryName("");
      setShowAddCategory(false);
      fetchDriverCategories();
    } catch (error) {
      toast.error(getErrorMsg ? getErrorMsg(error, "Failed to add category") : "Failed to add category");
    }
  };

  const filterBookings = () => {
    let filtered = [...bookings];

    if (statusFilter !== "all") {
      filtered = filtered.filter(b => b.status === statusFilter);
    }

    if (vehicleFilter !== "all") {
      filtered = filtered.filter(b => b.vehicle_type === vehicleFilter);
    }

    if (false) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(b =>
        b.customer_name?.toLowerCase().includes(query) ||
        b.customer_phone?.toLowerCase().includes(query) ||
        b.customer_email?.toLowerCase().includes(query) ||
        b.pickup_location?.address?.toLowerCase().includes(query) ||
        b.dropoff_location?.address?.toLowerCase().includes(query)
      );
    }

    setFilteredBookings(filtered);
  };

  const handleAssignDriver = async () => {
    if (selectedBookingIds.length > 1) {
      try { for (const bid of selectedBookingIds) { await axios.put(API + "/admin/bookings/" + bid + "/assign-driver", { driver_id: selectedDriver }); }
        toast.success("Assigned " + selectedBookingIds.length + " bookings");
        setSelectedBookingIds([]); setAssignDialogOpen(false); fetchBookings();
      } catch(e) { toast.error("Bulk assign failed"); }
      return;
    }
    if (!selectedDriver) {
      toast.error("Please select a driver");
      return;
    }

    try {
      await axios.post(`${API}/admin/bookings/${selectedBooking.id}/assign`, {
        driver_id: selectedDriver
      });
      toast.success("Driver assigned successfully!");
      setAssignDialogOpen(false);
      fetchBookings();
    } catch (error) {
      toast.error("Failed to assign driver");
    }
  };

  const toggleBookingSelect = (id) => {
    setSelectedBookingIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    if (selectedBookingIds.length === filteredBookings.length) setSelectedBookingIds([]);
    else setSelectedBookingIds(filteredBookings.map(b => b.id));
  };
  const handleBulkDelete = async () => {
    if (!selectedBookingIds.length) return;
    if (!window.confirm("Delete " + selectedBookingIds.length + " bookings?")) return;
    for (const id of selectedBookingIds) { try { await axios.delete(API + "/admin/bookings/" + id); } catch(e) {} }
    toast.success("Deleted"); setSelectedBookingIds([]); fetchBookings();
  };
  const handleBulkComplete = async () => {
    if (!selectedBookingIds.length) return;
    if (!window.confirm("Mark " + selectedBookingIds.length + " as completed?")) return;
    for (const id of selectedBookingIds) { try { await axios.put(API + "/admin/bookings/" + id, { status: "completed" }); } catch(e) {} }
    toast.success("Completed"); setSelectedBookingIds([]); fetchBookings();
  };
  const handleBulkPaid = async () => {
    if (!selectedBookingIds.length) return;
    if (!window.confirm("Mark " + selectedBookingIds.length + " as paid?")) return;
    for (const id of selectedBookingIds) {
      try { await axios.put(API + "/admin/bookings/" + id + "/pay-driver"); }
      catch(e) { try { await axios.put(API + "/admin/bookings/" + id, { payment_status: "paid" }); } catch(e2) {} }
    }
    toast.success("Marked paid"); setSelectedBookingIds([]); fetchBookings();
  };
  const handleBulkAssignOpen = () => {
    if (!selectedBookingIds.length) return;
    const first = filteredBookings.find(b => b.id === selectedBookingIds[0]);
    if (first) { setSelectedBooking(first); setAssignDialogOpen(true); }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`);
      toast.success('Booking deleted successfully!');
      fetchBookings();
    } catch (error) {
      toast.error(getErrorMsg(error, "Failed to delete booking"));
    }
  };

  const handleCompleteBooking = async (bookingId) => {
    try {
      const response = await fetch(`${API}/admin/bookings/${bookingId}/update-status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "completed" })
      });
      const data = await response.json();
      if (data.success) {
        toast.success("Booking marked as completed");
        fetchBookings();
      } else {
        toast.error("Failed to update");
      }
    } catch (error) {
      toast.error("Error updating booking");
    }
  };

  const openAssignDialog = (booking) => {
    setSelectedBooking(booking);
    setSelectedDriver(booking.driver_id || "");
    setAssignDialogOpen(true);
  };

  const handleAddDriver = async () => {
    // Validation - only basic required fields
    if (!newDriverData.name.trim() || !newDriverData.email.trim() || !newDriverData.phone.trim() || 
        !newDriverData.password.trim() || !newDriverData.address.trim() || !newDriverData.license_plate.trim()) {
      toast.error("Please fill in all required fields (Name, Email, Phone, Password, Address, Car Number)");
      return;
    }

    try {
      await axios.post(`${API}/admin/drivers`, newDriverData);
      toast.success("Driver added successfully! They can now login with their email and password.");
      setAddDriverDialogOpen(false);
      setNewDriverData({
        name: "",
        email: "",
        phone: "",
        password: "",
        address: "",
        vehicle_type: "saloon",
        license_plate: "",
        driver_type: "dsa",
        dvla_licence: "",
        dvla_licence_expiry: "",
        phd_licence: "",
        phd_licence_expiry: "",
        phv_licence: "",
        phv_licence_expiry: "",
        insurance_certificate: "",
        insurance_certificate_expiry: "",
        ni_number: "",
        bank_name: "",
        bank_account_number: "",
        bank_sort_code: "",
        car_make: "",
        car_model: "",
        car_colour: "",
        max_seat: 4
      });
      fetchDrivers();
    } catch (error) {
      toast.error(getErrorMsg(error, "Failed to add driver"));
    }
  };

  const openEditDialog = (driver) => {
    setEditingDriver({
      id: driver.id,
      name: driver.name || "",
      email: driver.email || "",
      phone: driver.phone || "",
      address: driver.address || "",
      vehicle_type: driver.vehicle_type,
      license_plate: driver.license_plate || "",
      driver_type: driver.driver_type || "dsa",
      driver_category: driver.driver_category || "",
      car_make: driver.car_make || "",
      car_model: driver.car_model || "",
      car_colour: driver.car_colour || "",
      max_seat: driver.max_seat || 4,
      ni_number: driver.ni_number || "",
      bank_name: driver.bank_name || "",
      bank_account_number: driver.bank_account_number || "",
      bank_sort_code: driver.bank_sort_code || "",
      profile_pic: driver.profile_pic || "",
      dvla_licence: driver.dvla_licence || "",
      dvla_licence_expiry: driver.dvla_licence_expiry || "",
      phd_licence: driver.phd_licence || "",
      phd_licence_expiry: driver.phd_licence_expiry || "",
      phv_licence: driver.phv_licence || "",
      phv_licence_expiry: driver.phv_licence_expiry || "",
      insurance_certificate: driver.insurance_certificate || "",
      insurance_certificate_expiry: driver.insurance_certificate_expiry || "",
      mot_certificate: driver.mot_certificate || "",
      mot_expiry: driver.mot_expiry || ""
    });
    setEditDriverDialogOpen(true);
  };

  const handleEditDriver = async () => {
    if (!editingDriver.name.trim() || !editingDriver.email.trim() ||
        !editingDriver.phone.trim() || !editingDriver.address.trim() || 
        !editingDriver.license_plate.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await axios.put(`${API}/admin/drivers/${editingDriver.id}`, {
        name: editingDriver.name,
        email: editingDriver.email,
        phone: editingDriver.phone,
        address: editingDriver.address,
        vehicle_type: editingDriver.vehicle_type,
        license_plate: editingDriver.license_plate,
        driver_type: editingDriver.driver_type || "dsa",
        driver_category: editingDriver.driver_category || "",
        car_make: editingDriver.car_make || "",
        car_model: editingDriver.car_model || "",
        car_colour: editingDriver.car_colour || "",
        profile_pic: editingDriver.profile_pic || "",
        phv_licence: editingDriver?.phv_licence || "",
        phv_licence_expiry: editingDriver?.phv_licence_expiry || "",
        dvla_licence: editingDriver.dvla_licence || "",
        dvla_licence_expiry: editingDriver.dvla_licence_expiry || "",
        phd_licence: editingDriver.phd_licence || "",
        phd_licence_expiry: editingDriver.phd_licence_expiry || "",
        insurance_certificate: editingDriver.insurance_certificate || "",
        insurance_certificate_expiry: editingDriver.insurance_certificate_expiry || "",
        mot_certificate: editingDriver.mot_certificate || "",
        mot_expiry: editingDriver.mot_expiry || ""
      });
      toast.success("Driver updated successfully!");
      setEditDriverDialogOpen(false);
      setEditingDriver(null);
      fetchDrivers();
    } catch (error) {
      toast.error(getErrorMsg(error, "Failed to update driver"));
    }
  };

  const openDeleteDialog = (driver) => {
    setDriverToDelete(driver);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDriver = async () => {
    try {
      await axios.delete(`${API}/admin/drivers/${driverToDelete.id}`);
      toast.success("Driver deleted successfully!");
      setDeleteDialogOpen(false);
      setDriverToDelete(null);
      fetchDrivers();
      fetchBookings(); // Refresh bookings as driver assignments may have changed
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to delete driver";
      
      // Check if it's an active bookings error
      if (errorMsg.includes("active booking")) {
        toast.error(errorMsg, { duration: 8000 });
        // Keep dialog open so user can see the error and close manually
      } else {
        toast.error(errorMsg);
        setDeleteDialogOpen(false);
        setDriverToDelete(null);
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-blue-1000",
      confirmed: "bg-blue-500",
      in_progress: "bg-green-500",
      completed: "bg-gray-500",
      cancelled: "bg-red-500",
    };
    return colors[status] || "bg-gray-500";
  };

  const getDriverStatus = (status) => {
    const colors = {
      available: "bg-green-500",
      busy: "bg-orange-500",
      offline: "bg-gray-500",
    };
    return colors[status] || "bg-gray-500";
  };

  const clearFilters = () => {
    setStatusFilter("all");
    setVehicleFilter("all");
    setSearchQuery("");
  };

  // Pricing Management Functions
  const fetchPricingConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/pricing`);
      setPricingConfig(response.data); setMileRanges(response.data.mile_ranges || []);
    } catch (error) {
      toast.error("Failed to fetch pricing configuration");
    }
  };

  const openEditPricingDialog = (vehicleType, pricing) => {
    setEditingPricing({
      vehicle_type: vehicleType,
      base_fare_10_miles: pricing.base_fare_10_miles,
      per_mile_after_10: pricing.per_mile_after_10
    });
    setEditPricingDialogOpen(true);
  };

  const handleUpdateBasePricing = async () => {
    if (!editingPricing.base_fare_10_miles || !editingPricing.per_mile_after_10) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      await axios.put(`${API}/admin/pricing/base`, editingPricing);
      toast.success("Pricing updated successfully!");
      setEditPricingDialogOpen(false);
      setEditingPricing(null);
      fetchPricingConfig();
    } catch (error) {
      toast.error(getErrorMsg(error, "Failed to update pricing"));
    }
  };

  const handleAddZipcodePricing = async () => {
    if (!newZipcodePricing.zipcode.trim() || !newZipcodePricing.base_fare_10_miles || !newZipcodePricing.per_mile_after_10) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      await axios.post(`${API}/admin/pricing/zipcode`, newZipcodePricing);
      toast.success("Zipcode pricing added successfully!");
      setAddZipcodeDialogOpen(false);
      setNewZipcodePricing({
        zipcode: "",
        vehicle_type: "saloon",
        base_fare_10_miles: 37.0,
        per_mile_after_10: 4.0
      });
      fetchPricingConfig();
    } catch (error) {
      toast.error(getErrorMsg(error, "Failed to add zipcode pricing"));
    }
  };

  const handleDeleteZipcodePricing = async (zipcode, vehicleType) => {
    if (!window.confirm(`Delete pricing for ${zipcode} - ${vehicleType.toUpperCase()}?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/pricing/zipcode/${zipcode}/${vehicleType}`);
      toast.success("Zipcode pricing deleted successfully!");
      fetchPricingConfig();
    } catch (error) {
      toast.error(getErrorMsg(error, "Failed to delete zipcode pricing"));
    }
  };

  // Discount Code Management functions
  const fetchDiscountCodes = async () => {
    try {
      const response = await axios.get(`${API}/admin/discount-codes`);
      setDiscountCodes(response.data.codes);
    } catch (error) {
      toast.error("Failed to fetch discount codes");
    }
  };

  const handleAddDiscount = async () => {
    if (!newDiscount.code.trim() || !newDiscount.value) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await axios.post(`${API}/admin/discount-codes`, newDiscount);
      toast.success("Discount code created successfully!");
      setAddDiscountDialogOpen(false);
      setNewDiscount({
        code: "",
        type: "percentage",
        value: 0,
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        max_uses_per_user: 1,
        is_active: true
      });
      fetchDiscountCodes();
    } catch (error) {
      toast.error(getErrorMsg(error, "Failed to create discount code"));
    }
  };

  const handleUpdateDiscount = async () => {
    if (!editingDiscount.code.trim() || !editingDiscount.value) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await axios.put(`${API}/admin/discount-codes/${editingDiscount.id}`, editingDiscount);
      toast.success("Discount code updated successfully!");
      setEditDiscountDialogOpen(false);
      setEditingDiscount(null);
      fetchDiscountCodes();
    } catch (error) {
      toast.error(getErrorMsg(error, "Failed to update discount code"));
    }
  };

  const handleDeleteDiscount = async (codeId) => {
    if (!window.confirm('Are you sure you want to delete this discount code?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/discount-codes/${codeId}`);
      toast.success("Discount code deleted successfully!");
      fetchDiscountCodes();
    } catch (error) {
      toast.error(getErrorMsg(error, "Failed to delete discount code"));
    }
  };

  const openEditDiscountDialog = (discount) => {
    setEditingDiscount(discount);
    setEditDiscountDialogOpen(true);
  };

  // Contract Management functions
  const fetchContracts = async () => {
    try {
      const response = await axios.get(`${API}/admin/contracts`);
      if (response.data.contracts) {
        setContracts(response.data.contracts);
      }
    } catch (error) {
      console.log("No contracts yet");
    }
  };

  const handleAddContract = async () => {
    // Validate required fields
    if (!newContract.contract_title.trim() || !newContract.contract_number.trim() || !newContract.customer_id) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!newContract.pickup_location.trim() || !newContract.drop_location.trim()) {
      toast.error("Please provide pickup and drop locations");
      return;
    }

    // Validate Round Trip fields if selected
    if (newContract.ride_type === "Round Trip") {
      if (!newContract.return_pickup_location.trim() || !newContract.return_drop_location.trim()) {
        toast.error("Please provide return trip locations");
        return;
      }
    }

    try {
      await axios.post(`${API}/admin/contracts`, newContract);
      toast.success("Contract added successfully!");
      setAddContractDialogOpen(false);
      setNewContract({
        contract_type: "DSA",
        contract_title: "",
        contract_number: "",
        customer_id: "",
        ride_type: "One Way",
        service_type: "Airport Transport",
        car_type: "saloon",
        max_passengers: 4,
        total_miles: 0,
        total_fare: 0,
        pickup_location: "",
        drop_location: "",
        pickup_time: "",
        drop_time: "",
        return_pickup_location: "",
        return_drop_location: "",
        return_pickup_time: "",
        return_drop_time: "",
        payment_type: "flat",
    payment_type: "flat",
    driver_payable: 0,
        roman_cars_payment: 0,
        status: "active"
      });
      fetchContracts();
    } catch (error) {
      toast.error(getErrorMsg(error, "Failed to add contract"));
    }
  };

  // Customer Management functions
  const fetchCustomers = async () => {
    try {
      // Try to fetch from backend first
      const response = await axios.get(`${API}/admin/customers`);
      if (response.data.customers) {
        setCustomers(response.data.customers);
        return;
      }
    } catch (error) {
      console.log("No customers endpoint, extracting from bookings");
    }

    try {
      // Fallback: Extract unique customers from bookings
      const uniqueCustomers = [];
      const customerEmails = new Set();
      
      bookings.forEach(booking => {
        if (booking.customer_email && !customerEmails.has(booking.customer_email)) {
          customerEmails.add(booking.customer_email);
          uniqueCustomers.push({
            id: booking.customer_email, // Use email as ID for now
            name: booking.customer_name,
            email: booking.customer_email,
            phone: booking.customer_phone,
            address: booking.customer_address || 'N/A',
            total_bookings: bookings.filter(b => b.customer_email === booking.customer_email).length,
            total_spent: bookings.filter(b => b.customer_email === booking.customer_email && b.status === 'completed').reduce((sum, b) => sum + (b.price || 0), 0)
          });
        }
      });
      
      setCustomers(uniqueCustomers);
    } catch (error) {
      console.log("Error fetching customers");
    }
  };

  const handleAddCustomer = async () => {
    if (!newCustomer.name.trim()) {
      toast.error("Please fill in customer name");
      return;
    }

    try {
      await axios.post(`${API}/admin/customers`, newCustomer);
      toast.success("Customer added successfully!");
      setAddCustomerDialogOpen(false);
      setNewCustomer({
        name: "",
        email: "",
        phone: "",
        address: ""
      });
      fetchCustomers();
    } catch (error) {
      toast.error(getErrorMsg(error, "Failed to add customer"));
    }
  };

  const handleAddBulkRide = () => {
    if (contracts.length === 0) {
      toast.error("Please create a contract first before adding bulk rides");
      return;
    }
    toast.info("Bulk ride upload will allow CSV import of multiple bookings");
    setAddBulkRideDialogOpen(false);
  };

  // Trip Discount Management functions
  const fetchTripDiscounts = async () => {
    try {
      const response = await axios.get(`${API}/admin/trip-discounts`);
      setTripDiscounts(response.data.discounts);
    } catch (error) {
      toast.error("Failed to fetch trip discounts");
    }
  };

  const handleUpdateTripDiscount = async (tripType, field, value) => {
    const updated = {
      ...tripDiscounts,
      [tripType]: {
        ...tripDiscounts[tripType],
        [field]: value
      }
    };
    
    try {
      await axios.put(`${API}/admin/trip-discounts`, updated);
      setTripDiscounts(updated);
      toast.success("Trip discount updated successfully!");
    } catch (error) {
      toast.error(getErrorMsg(error, "Failed to update trip discount"));
    }
  };

  return (
    <div className="admin-dashboard" data-testid="admin-dashboard">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div>
            <h1>Admin Dashboard</h1>
            <p>Manage Bookings, Drivers & Pricing</p>
          </div>
        </div>

        {/* Statistics */}
        <div className="stats-grid">
          <Card>
            <CardContent className="stat-card">
              <div className="stat-value">{totalBookings}</div>
              <div className="stat-label">Total Bookings</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="stat-card">
              <div className="stat-value">{bookingStats.pending}</div>
              <div className="stat-label">Pending</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="stat-card">
              <div className="stat-value">{bookingStats.in_progress}</div>
              <div className="stat-label">In Progress</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="stat-card">
              <div className="stat-value">{drivers.filter(d => d.status === "available").length}</div>
              <div className="stat-label">Available Drivers</div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="overview" className="mt-6">
          <div className="overflow-x-auto pb-2">
            <TabsList className="inline-flex w-max md:w-full md:grid md:grid-cols-7 gap-1 min-w-full">
              <TabsTrigger value="overview" className="text-xs md:text-sm whitespace-nowrap px-3">Overview</TabsTrigger>
              <TabsTrigger value="bookings" className="text-xs md:text-sm whitespace-nowrap px-3">Bookings</TabsTrigger>
              <TabsTrigger value="drivers" className="text-xs md:text-sm whitespace-nowrap px-3">Drivers</TabsTrigger>
              <TabsTrigger value="pricing" className="text-xs md:text-sm whitespace-nowrap px-3">Pricing</TabsTrigger>
              <TabsTrigger value="discounts" className="text-xs md:text-sm whitespace-nowrap px-3">Discounts</TabsTrigger>
              <TabsTrigger value="invoices" className="text-xs md:text-sm whitespace-nowrap px-3">Invoices</TabsTrigger>
              <TabsTrigger value="settings" className="text-xs md:text-sm whitespace-nowrap px-3">
                <Settings size={14} className="mr-1" />
                Settings
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="space-y-6">
              {/* Earnings Summary */}
              <Card>
                <CardHeader>
                  <CardTitle>Total Earnings Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <PoundSterling className="mx-auto mb-2 text-green-600" size={32} />
                      <p className="text-sm text-gray-600">Total Revenue</p>
                      <p className="text-3xl font-bold text-green-600">
                        £{bookingStats.total_revenue.toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">From {totalBookings} bookings</p>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <Car className="mx-auto mb-2 text-blue-600" size={32} />
                      <p className="text-sm text-gray-600">Completed Rides</p>
                      <p className="text-3xl font-bold text-blue-600">
                        {bookingStats.completed}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Successfully completed</p>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <PoundSterling className="mx-auto mb-2 text-purple-600" size={32} />
                      <p className="text-sm text-gray-600">Avg. Fare</p>
                      <p className="text-3xl font-bold text-purple-600">
                        £{totalBookings > 0 ? (bookingStats.total_revenue / totalBookings).toFixed(2) : "0.00"}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Per booking</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Future Rides */}
              <Card>
                <CardHeader>
                  <CardTitle>Upcoming Rides ({bookings.filter(b => {
                    try {
                      const bookingDate = new Date(b.booking_date + ' ' + b.booking_time);
                      return bookingDate >= new Date();
                    } catch {
                      return false;
                    }
                  }).length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {bookings.filter(b => {
                    try {
                      const bookingDate = new Date(b.booking_date + ' ' + b.booking_time);
                      return bookingDate >= new Date();
                    } catch {
                      return false;
                    }
                  }).length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No upcoming rides scheduled
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {bookings.filter(b => {
                        try {
                          const bookingDate = new Date(b.booking_date + ' ' + b.booking_time);
                          return bookingDate >= new Date();
                        } catch {
                          return false;
                        }
                      }).slice(0, 10).map((booking) => (
                        <div key={booking.id} className="border rounded-lg p-4 hover:bg-gray-50">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <Badge>{booking.status}</Badge>
                              <p className="text-sm text-gray-500 mt-1">#{booking.id?.substring(0, 8)}</p>
                            </div>
                            <p className="font-bold text-lg">£{booking.price?.toFixed(2)}</p>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                            <div>
                              <p className="text-gray-600"><strong>Customer:</strong> {booking.customer_name}</p>
                              <p className="text-gray-600"><strong>Phone:</strong> {booking.customer_phone}</p>
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <Calendar size={14} />
                                <span>{booking.booking_date}</span>
                                <Clock size={14} />
                                <span>{booking.booking_time}</span>
                              </div>
                              <p className="text-gray-600 mt-1"><strong>Vehicle:</strong> {booking.vehicle_type?.toUpperCase()}</p>
                            </div>
                          </div>
                          <div className="mt-2 text-sm">
                            <p className="text-gray-600"><MapPin size={14} className="inline mr-1" /><strong>From:</strong> {booking.pickup_location?.address}</p>
                            <p className="text-gray-600"><MapPin size={14} className="inline mr-1" /><strong>To:</strong> {booking.dropoff_location?.address}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Past Rides */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Past Rides ({bookings.filter(b => {
                    try {
                      const bookingDate = new Date(b.booking_date + ' ' + b.booking_time);
                      return bookingDate < new Date();
                    } catch {
                      return false;
                    }
                  }).length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {bookings.filter(b => {
                    try {
                      const bookingDate = new Date(b.booking_date + ' ' + b.booking_time);
                      return bookingDate < new Date();
                    } catch {
                      return false;
                    }
                  }).length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No past rides found
                    </div>
                  ) : (
                    <div className="bookings-table-container">
                      <table className="bookings-table">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Customer</th>
                            <th>Route</th>
                            <th>Date & Time</th>
                            <th>Vehicle</th>
                            <th>Price</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bookings.filter(b => {
                            try {
                              const bookingDate = new Date(b.booking_date + ' ' + b.booking_time);
                              return bookingDate < new Date();
                            } catch {
                              return false;
                            }
                          }).slice(0, 20).map((booking) => (
                            <tr key={booking.id}>
                              <td>#{booking.id?.substring(0, 8)}</td>
                              <td>
                                <div>
                                  <div className="font-semibold">{booking.customer_name}</div>
                                  <div className="text-sm text-gray-600">{booking.customer_phone}</div>
                                </div>
                              </td>
                              <td>
                                <div className="text-sm">
                                  <div className="text-green-600">📍 {booking.pickup_location?.address}</div>
                                  <div className="text-red-600">📍 {booking.dropoff_location?.address}</div>
                                </div>
                              </td>
                              <td>
                                <div>
                                  <div>{booking.booking_date}</div>
                                  <div className="text-sm text-gray-600">{booking.booking_time}</div>
                                </div>
                              </td>
                              <td>
                                <Badge variant="outline">{booking.vehicle_type?.toUpperCase()}</Badge>
                              </td>
                              <td className="font-bold text-green-600">£{booking.price?.toFixed(2)}</td>
                              <td>
                                <Badge variant={
                                  booking.status === 'completed' ? 'default' :
                                  booking.status === 'pending' ? 'secondary' :
                                  booking.status === 'confirmed' ? 'default' : 'outline'
                                }>
                                  {booking.status}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <div>
              {/* Filters */}
              <Card className="filters-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter size={20} />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="filters-section">
              <div className="filter-group">
                <Label>Search</Label>
                <div className="search-input-wrapper">
                  <Search size={16} />
                  <Input
                    placeholder="Search by customer name, phone, email, or location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="search-input"
                  />
                </div>
              </div>

              <div className="filter-group">
                <Label>Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="filter-group">
                <Label>Vehicle Type</Label>
                <Select value={vehicleFilter} onValueChange={setVehicleFilter}>
                  <SelectTrigger data-testid="vehicle-filter">
                    <SelectValue placeholder="All Vehicles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Vehicles</SelectItem>
                    <SelectItem value="saloon">Saloon</SelectItem>
                    <SelectItem value="estate">Estate</SelectItem>
                    <SelectItem value="mvp">MVP</SelectItem>
                    <SelectItem value="luxury">Luxury</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="filter-group">
                <Button variant="outline" onClick={clearFilters} data-testid="clear-filters">
                  Clear Filters
                </Button>
              </div>
            </div>

            <div className="filter-results">
              Showing page {currentPage} of {totalPages} ({totalBookings} total bookings)
            <div className="flex gap-2 mt-2">
              <Button size="sm" variant="outline" disabled={currentPage <= 1} onClick={() => fetchBookings(currentPage - 1)}>Previous</Button>
              <span className="px-3 py-1 text-sm font-medium">Page {currentPage} / {totalPages}</span>
              <Button size="sm" variant="outline" disabled={currentPage >= totalPages} onClick={() => fetchBookings(currentPage + 1)}>Next</Button>
            </div>
            </div>
          </CardContent>
        </Card>

        {/* Bookings Table */}
        <div className="admin-section">
          <h2>All Bookings</h2>
            {selectedBookingIds.length > 0 && (<div style={{display:"flex",gap:"8px",alignItems:"center",padding:"10px",background:"#fef3c7",borderRadius:"6px",margin:"10px 0"}}><span style={{fontWeight:"bold"}}>{selectedBookingIds.length} selected</span><button onClick={handleBulkAssignOpen} style={{padding:"6px 12px",background:"#3b82f6",color:"#fff",border:"none",borderRadius:"4px",cursor:"pointer"}}>Assign Driver</button><button onClick={handleBulkPaid} style={{padding:"6px 12px",background:"#10b981",color:"#fff",border:"none",borderRadius:"4px",cursor:"pointer"}}>Mark Paid</button><button onClick={handleBulkComplete} style={{padding:"6px 12px",background:"#8b5cf6",color:"#fff",border:"none",borderRadius:"4px",cursor:"pointer"}}>Mark Completed</button><button onClick={handleBulkDelete} style={{padding:"6px 12px",background:"#ef4444",color:"#fff",border:"none",borderRadius:"4px",cursor:"pointer"}}>Delete</button><button onClick={() => setSelectedBookingIds([])} style={{padding:"6px 12px",background:"#9ca3af",color:"#fff",border:"none",borderRadius:"4px",cursor:"pointer"}}>Clear</button></div>)}
          {loading ? (
            <p>Loading...</p>
          ) : filteredBookings.length === 0 ? (
            <Card>
              <CardContent className="empty-state">
                <p>No bookings found</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bookings-table-card">
              <div className="bookings-table-container">
                <div style={{display:"flex",gap:"8px",alignItems:"center",padding:"10px",background:"#f3f4f6",borderRadius:"6px",margin:"10px 0",flexWrap:"wrap"}} data-testid="bookings-date-filter-row"><span style={{fontWeight:"600",fontSize:"13px",color:"#374151"}}>Date:</span><input type="date" data-testid="booking-from-date" value={bookingFromDate} onChange={(e)=>setBookingFromDate(e.target.value)} style={{padding:"4px 8px",border:"1px solid #d1d5db",borderRadius:"4px",fontSize:"13px"}} /><span style={{fontSize:"13px",color:"#6b7280"}}>to</span><input type="date" data-testid="booking-to-date" value={bookingToDate} onChange={(e)=>setBookingToDate(e.target.value)} style={{padding:"4px 8px",border:"1px solid #d1d5db",borderRadius:"4px",fontSize:"13px"}} /><button data-testid="booking-sort-toggle" onClick={()=>setBookingSortOrder(bookingSortOrder==="asc"?"desc":"asc")} style={{padding:"6px 12px",background:"#6366f1",color:"#fff",border:"none",borderRadius:"4px",cursor:"pointer",fontSize:"13px"}}>{bookingSortOrder==="asc"?"↑ Asc":"↓ Desc"}</button><button data-testid="booking-filter-clear" onClick={()=>{setBookingFromDate("");setBookingToDate("");setBookingSortOrder("desc");}} style={{padding:"6px 12px",background:"#9ca3af",color:"#fff",border:"none",borderRadius:"4px",cursor:"pointer",fontSize:"13px"}}>Clear</button></div>
              <table className="bookings-table" data-testid="bookings-table">
                  <thead>
                    <tr>
                      <th style={{width:"30px",textAlign:"center"}}><input type="checkbox" data-testid="booking-select-all" checked={(function(){var f=filteredBookings.filter(function(b){var d=b.booking_date||"";if(bookingFromDate&&d<bookingFromDate)return false;if(bookingToDate&&d>bookingToDate)return false;return true;});return f.length>0 && selectedBookingIds.length===f.length;})()} onChange={(e)=>{if(e.target.checked){var f=filteredBookings.filter(function(b){var d=b.booking_date||"";if(bookingFromDate&&d<bookingFromDate)return false;if(bookingToDate&&d>bookingToDate)return false;return true;});setSelectedBookingIds(f.map(function(b){return b.id;}));}else{setSelectedBookingIds([]);}}} /></th>
                    <th>ID</th>
                      <th>Customer</th>
                      <th>Contact</th>
                      <th>Route</th>
                      <th>Date & Time</th>
                      <th>Vehicle</th>
                      <th>Trip Type</th>
                      <th>Price</th>
                      <th>Driver</th>
                      <th>Status</th>
                      <th style={{minWidth: "120px"}}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...filteredBookings].filter(function(b){
                  var d = b.booking_date || "";
                  if (bookingFromDate && d < bookingFromDate) return false;
                  if (bookingToDate && d > bookingToDate) return false;
                  return true;
                }).sort(function(x,y){
                  var da = (x.booking_date || "") + " " + (x.booking_time || "");
                  var db = (y.booking_date || "") + " " + (y.booking_time || "");
                  return bookingSortOrder === "asc" ? da.localeCompare(db) : db.localeCompare(da);
                }).map((booking) => {
                      const driver = drivers.find(d => d.id === booking.driver_id);
                      return (
                        <tr key={booking.id} data-testid={`booking-row-${booking.id}`}><td onClick={(e)=>e.stopPropagation()}><input type="checkbox" checked={selectedBookingIds.includes(booking.id)} onChange={()=>toggleBookingSelect(booking.id)} /></td>
                          <td className="booking-id">#{booking.id.slice(0, 8)}</td>
                          <td>
                            <div className="customer-cell">
                              <User size={14} />
                              <span>{booking.customer_name || "N/A"}</span>
                            </div>
                          </td>
                          <td>
                            <div className="contact-cell">
                              <div><Phone size={12} /> {booking.customer_phone || "N/A"}</div>
                              <div><Mail size={12} /> {booking.customer_email || "N/A"}</div>
                            </div>
                          </td>
                          <td>
                            <div className="route-cell">
                              <div className="route-from">
                                <MapPin size={12} className="text-green-600" />
                                {booking.pickup_location?.address || "N/A"}
                              </div>
                              <div className="route-to">
                                <MapPin size={12} className="text-red-600" />
                                {booking.dropoff_location?.address || "N/A"}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="datetime-cell">
                              <div><Calendar size={12} /> {booking.booking_date}</div>
                              <div><Clock size={12} /> {booking.booking_time}</div>
                            </div>
                          </td>
                          <td className="vehicle-cell">{booking.vehicle_type.toUpperCase()}</td>
                          <td>{booking.trip_type === "round_trip" ? "Round Trip" : "One Way"}</td>
                          <td className="price-cell">£{(booking.price || 0).toFixed(2)}</td>
                          <td>
                            {driver ? (
                              <div className="driver-cell">
                                <Car size={12} />
                                <span>{driver.license_plate}</span>
                              </div>
                            ) : (
                              <span className="text-gray-400">Not assigned</span>
                            )}
                          </td>
                          <td>
                            <Badge className={getStatusColor(booking.status)}>
                              {booking.status}
                            </Badge>
                          </td>
                          <td>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => openAssignDialog(booking)}
                                variant={driver ? "default" : "outline"}
                                size="sm"
                                className={driver ? "bg-blue-600 hover:bg-blue-700" : ""}
                                data-testid={`assign-driver-${booking.id}`}
                              >
                                <Car size={14} className="mr-1" />
                                {driver ? "Reassign" : "Assign Driver"}
                              </Button>
                            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white mr-2" onClick={() => handleCompleteBooking(booking.id)}>Complete</Button>
                    <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white mr-2" onClick={() => { const driverAmt = booking.driver_payable > 0 ? booking.driver_payable : (booking.paid_amount > 0 ? booking.paid_amount : Math.round((booking.price || 0) * 0.25 * 100) / 100); if (booking.driver_payable > 0 || booking.is_bulk_booking || booking.contract_id) { const _c = (contracts || []).find(c => c.id === booking.contract_id); const _pt = (_c && _c.payment_type) || booking.payment_type; const _dp = (_c && _c.driver_payable) || booking.driver_payable; const autoAmt = _pt === "flat" ? (_dp || driverAmt) : (_dp > 0 ? Math.round((booking.price || 0) * _dp / 100 * 100) / 100 : driverAmt); axios.put(`${API}/admin/bookings/${booking.id}/pay-driver`, { paid_amount: autoAmt }).then(() => { toast.success("Payment updated!"); fetchBookings(); }).catch(() => toast.error("Failed")); return; } const amt = window.prompt("Enter paid amount:", driverAmt); if (amt !== null) { axios.put(`${API}/admin/bookings/${booking.id}/pay-driver`, { paid_amount: parseFloat(amt) }).then(() => { toast.success("Payment updated!"); fetchBookings(); }).catch(() => toast.error("Failed")); } }}>{booking.driver_paid ? "Paid" : "Pay"}</Button>
                              <Button
                                onClick={() => handleDeleteBooking(booking.id)}
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
            </div>
          </TabsContent>

          {/* Drivers Management Tab */}
          <TabsContent value="drivers">
            <div className="mb-4 flex justify-between items-center">
              <Select value={driverCategoryFilter} onValueChange={(v) => setDriverCategoryFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-56 bg-green-600 text-white border-green-600 hover:bg-green-700 font-semibold [bg-green-600 text-white border-green-600 hover:bg-green-700 font-semibold>span]:text-white"><SelectValue placeholder="All Categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {driverCategories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <h2>Drivers Management</h2>
              <Button 
                onClick={() => setAddDriverDialogOpen(true)}
                className="bg-green-600 hover:bg-green-700"
              >
                <Car size={16} className="mr-2" />
                Add New Driver
              </Button>
            </div>
          {drivers.length === 0 ? (
            <Card>
              <CardContent className="empty-state">
                <Car size={48} className="text-gray-400" />
                <p>No drivers found</p>
                <Button onClick={() => setAddDriverDialogOpen(true)}>Add First Driver</Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="bookings-table-card">
              <div className="bookings-table-container">
                <table className="bookings-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Address</th>
                      <th>Vehicle</th>
                      <th>License Plate</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drivers.filter(d => !driverCategoryFilter || d.driver_category === driverCategoryFilter).map((driver) => (
                <tr key={driver.id} style={{backgroundColor: (() => { const now = new Date(); const expiries = [driver.dvla_licence_expiry, driver.phd_licence_expiry, driver.phv_licence_expiry, driver.insurance_certificate_expiry, driver.mot_expiry].filter(Boolean).map(d => new Date(d)); if (!expiries.length) return "transparent"; const closest = Math.min(...expiries.map(d => d - now)); const days = closest / (1000*60*60*24); if (days <= 7) return "#fca5a5"; if (days <= 15) return "#d97706"; if (days <= 30) return "#fb923c"; return "transparent"; })()}}>
                        <td>
                          <div className="customer-cell">
                            <User size={14} />
                            <span>{driver.name || "Unknown"}</span>
                          </div>
                        </td>
                        <td>
                          <div className="contact-cell">
                            <Phone size={12} /> {driver.phone || "N/A"}
                          </div>
                        </td>
                        <td>
                          <div className="text-sm">{driver.address || "N/A"}</div>
                        </td>
                        <td className="vehicle-cell">{driver.vehicle_type?.toUpperCase()}</td>
                        <td>
                          <div className="driver-cell">
                            <Car size={12} />
                            <span>{driver.license_plate}</span>
                          </div>
                        </td>
                        <td>
                          <Badge className={driver.status === "available" ? "bg-green-500" : "bg-orange-500"}>
                            {driver.status}
                          </Badge>
                        </td>
                        <td>
                          <div className="action-buttons-inline">
                    <Button onClick={() => { setViewingDriver(driver); setViewDriverDialogOpen(true); }} variant="outline" size="sm" className="mr-2 text-blue-600"><Eye size={14} className="mr-1" />View</Button>
                            <Button
                              onClick={() => openEditDialog(driver)}
                              variant="outline"
                              size="sm"
                              className="mr-2"
                            >
                              <Edit size={14} className="mr-1" />
                              Edit
                            </Button>
                            <Button
                              onClick={() => openDeleteDialog(driver)}
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 size={14} className="mr-1" />
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
          </TabsContent>

          {/* Price Management Tab */}
          <TabsContent value="pricing">
            <div className="mb-4 flex justify-between items-center">
              <h2>Price Management</h2>
              <Button 
                onClick={() => setAddZipcodeDialogOpen(true)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <PoundSterling size={16} className="mr-2" />
                Add Zipcode Pricing
              </Button>
            </div>

            {/* Mile Range Pricing Section */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-purple-700">Mile Range Pricing</CardTitle>
                <p className="text-sm text-gray-500">Set flexible pricing based on distance. First row can be fixed price, others per-mile.</p>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead className="bg-purple-100">
                      <tr>
                        <th className="border border-purple-200 p-2 text-left">From (Miles)</th>
                        <th className="border border-purple-200 p-2 text-left">To (Miles)</th>
                        <th className="border border-purple-200 p-2 text-left">Type</th>
                        <th className="border border-purple-200 p-2 text-center">Saloon (£)</th>
                        <th className="border border-purple-200 p-2 text-center">Estate (£)</th>
                        <th className="border border-purple-200 p-2 text-center">Luxury (£)</th>
                        <th className="border border-purple-200 p-2 text-center">MPV (£)</th>
                        <th className="border border-purple-200 p-2 text-center">Executive (£)</th>
                        <th className="border border-purple-200 p-2 text-center">Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mileRanges.map((range, idx) => (
                        <tr key={range.id} className="hover:bg-gray-50">
                          <td className="border p-1"><Input type="number" className="w-20 text-center" value={range.from_miles} onChange={(e) => { const u = [...mileRanges]; u[idx].from_miles = parseFloat(e.target.value) || 0; setMileRanges(u); }}/></td>
                          <td className="border p-1"><Input type="number" className="w-20 text-center" value={range.to_miles} onChange={(e) => { const u = [...mileRanges]; u[idx].to_miles = parseFloat(e.target.value) || 0; setMileRanges(u); }}/></td>
                          <td className="border p-1">
                            <Select value={range.price_type} onValueChange={(v) => { const u = [...mileRanges]; u[idx].price_type = v; setMileRanges(u); }}>
                              <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                              <SelectContent><SelectItem value="fixed">Fixed</SelectItem><SelectItem value="per_mile">Per Mile</SelectItem></SelectContent>
                            </Select>
                          </td>
                          <td className="border p-1"><Input type="number" step="0.01" className="w-20 text-center" value={range.saloon} onChange={(e) => { const u = [...mileRanges]; u[idx].saloon = parseFloat(e.target.value) || 0; setMileRanges(u); }}/></td>
                          <td className="border p-1"><Input type="number" step="0.01" className="w-20 text-center" value={range.estate} onChange={(e) => { const u = [...mileRanges]; u[idx].estate = parseFloat(e.target.value) || 0; setMileRanges(u); }}/></td>
                          <td className="border p-1"><Input type="number" step="0.01" className="w-20 text-center" value={range.luxury} onChange={(e) => { const u = [...mileRanges]; u[idx].luxury = parseFloat(e.target.value) || 0; setMileRanges(u); }}/></td>
                          <td className="border p-1"><Input type="number" step="0.01" className="w-20 text-center" value={range.mpv} onChange={(e) => { const u = [...mileRanges]; u[idx].mpv = parseFloat(e.target.value) || 0; setMileRanges(u); }}/></td>
                          <td className="border p-1"><Input type="number" step="0.01" className="w-20 text-center" value={range.executive} onChange={(e) => { const u = [...mileRanges]; u[idx].executive = parseFloat(e.target.value) || 0; setMileRanges(u); }}/></td>
                          <td className="border p-1 text-center"><Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => { if (mileRanges.length > 1) setMileRanges(mileRanges.filter((_, i) => i !== idx)); else toast.error("Need at least one range"); }}><Trash2 size={14} /></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" onClick={() => { const last = mileRanges[mileRanges.length - 1]; setMileRanges([...mileRanges, { id: Date.now().toString(), from_miles: last ? last.to_miles : 0, to_miles: last ? last.to_miles + 10 : 10, price_type: 'per_mile', saloon: 3, estate: 4, luxury: 6, mpv: 5, executive: 7 }]); }}><Plus size={16} className="mr-1" /> Add Range</Button>
                <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => { axios.put(`${API}/admin/pricing/mile-ranges`, { mile_ranges: mileRanges }).then(() => toast.success("Mile ranges saved!")).catch(() => toast.error("Failed to save")); }}>Save Mile Ranges</Button>
                </div>
              </CardContent>
            </Card>

            {/* Zipcode Pricing Section */}
            <Card>
              <CardHeader>
                <CardTitle>Zipcode-Specific Pricing</CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(pricingConfig.zipcode_pricing || {}).length === 0 ? (
                  <div className="empty-state">
                    <p>No zipcode-specific pricing configured</p>
                    <Button onClick={() => setAddZipcodeDialogOpen(true)} className="mt-4">
                      Add Zipcode Pricing
                    </Button>
                  </div>
                ) : (
                  <div className="bookings-table-container">
                    <table className="bookings-table">
                      <thead>
                        <tr>
                          <th>Zipcode</th>
                          <th>Vehicle Type</th>
                          <th>Base Fare (First 10 Miles)</th>
                          <th>Per Mile (After 10 Miles)</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(pricingConfig.zipcode_pricing || {}).map(([key, pricing]) => (
                          <tr key={key}>
                            <td className="font-semibold">{pricing.zipcode}</td>
                            <td>{pricing.vehicle_type.toUpperCase()}</td>
                            <td>£{(pricing.base_fare_10_miles || 0).toFixed(2)}</td>
                            <td>£{(pricing.per_mile_after_10 || 0).toFixed(2)}</td>
                            <td>
                              <Button
                                onClick={() => handleDeleteZipcodePricing(pricing.zipcode, pricing.vehicle_type)}
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 size={14} className="mr-1" />
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Discounts Tab - Trip-Based Discounts */}
          <TabsContent value="discounts">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Tag size={20} />
                  Trip-Based Discounts (One Way vs Round Trip)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* One Way Discount */}
                  <Card className="border-2 border-blue-200">
                    <CardHeader className="bg-blue-50">
                      <CardTitle className="text-xl flex items-center gap-2">
                        <span className="text-2xl">🚗</span>
                        One Way Discount
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 pt-4">
                      <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded">
                        <input
                          type="checkbox"
                          id="one-way-enabled"
                          checked={tripDiscounts.one_way.enabled}
                          onChange={(e) => handleUpdateTripDiscount('one_way', 'enabled', e.target.checked)}
                          className="rounded w-5 h-5"
                        />
                        <Label htmlFor="one-way-enabled" className="font-semibold text-lg cursor-pointer">Enable One Way Discount</Label>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">Discount Type</Label>
                        <Select 
                          value={tripDiscounts.one_way.type} 
                          onValueChange={(value) => handleUpdateTripDiscount('one_way', 'type', value)}
                        >
                          <SelectTrigger className="h-12">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage">Percentage (%)</SelectItem>
                            <SelectItem value="amount">Fixed Amount (£)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">Discount Value</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={tripDiscounts.one_way.value}
                          onChange={(e) => handleUpdateTripDiscount('one_way', 'value', parseFloat(e.target.value) || 0)}
                          className="text-2xl font-bold h-16 text-center"
                        />
                        <p className="text-sm text-gray-600 text-center p-2 bg-gray-50 rounded">
                          {tripDiscounts.one_way.type === 'percentage' 
                            ? `${tripDiscounts.one_way.value}% off all one-way trips`
                            : `£${tripDiscounts.one_way.value.toFixed(2)} off all one-way trips`}
                        </p>
                      </div>

                      {tripDiscounts.one_way.enabled && (
                        <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
                          <p className="text-sm text-green-800 font-semibold flex items-center gap-2">
                            <span className="text-xl">✓</span>
                            Active: Users see red tag "-£{tripDiscounts.one_way.type === 'amount' ? tripDiscounts.one_way.value.toFixed(2) : 'X.XX'}" on all one-way options
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Round Trip Discount */}
                  <Card className="border-2 border-purple-200">
                    <CardHeader className="bg-purple-50">
                      <CardTitle className="text-xl flex items-center gap-2">
                        <span className="text-2xl">🔄</span>
                        Round Trip Discount
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 pt-4">
                      <div className="flex items-center space-x-2 p-3 bg-purple-50 rounded">
                        <input
                          type="checkbox"
                          id="round-trip-enabled"
                          checked={tripDiscounts.round_trip.enabled}
                          onChange={(e) => handleUpdateTripDiscount('round_trip', 'enabled', e.target.checked)}
                          className="rounded w-5 h-5"
                        />
                        <Label htmlFor="round-trip-enabled" className="font-semibold text-lg cursor-pointer">Enable Round Trip Discount</Label>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">Discount Type</Label>
                        <Select 
                          value={tripDiscounts.round_trip.type} 
                          onValueChange={(value) => handleUpdateTripDiscount('round_trip', 'type', value)}
                        >
                          <SelectTrigger className="h-12">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage">Percentage (%)</SelectItem>
                            <SelectItem value="amount">Fixed Amount (£)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">Discount Value</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={tripDiscounts.round_trip.value}
                          onChange={(e) => handleUpdateTripDiscount('round_trip', 'value', parseFloat(e.target.value) || 0)}
                          className="text-2xl font-bold h-16 text-center"
                        />
                        <p className="text-sm text-gray-600 text-center p-2 bg-gray-50 rounded">
                          {tripDiscounts.round_trip.type === 'percentage' 
                            ? `${tripDiscounts.round_trip.value}% off all round trips`
                            : `£${tripDiscounts.round_trip.value.toFixed(2)} off all round trips`}
                        </p>
                      </div>

                      {tripDiscounts.round_trip.enabled && (
                        <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
                          <p className="text-sm text-green-800 font-semibold flex items-center gap-2">
                            <span className="text-xl">✓</span>
                            Active: Users see red tag "-£{tripDiscounts.round_trip.type === 'amount' ? tripDiscounts.round_trip.value.toFixed(2) : 'X.XX'}" on all round trip options
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>ℹ️ How it works:</strong> When customers book a ride, they will automatically see red discount tags on the vehicle prices showing the savings for one-way and round trip options. This helps them decide which option is better!
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <div className="space-y-6">
              {/* Action Buttons Row */}
              <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200">
                <CardContent className="pt-6">
                  <div className="flex gap-3">
                    <Select value={addDropdownValue} onValueChange={(value) => {
                      if (value === 'driver') setAddDriverDialogOpen(true);
                      else if (value === 'booking') setBookingListDialogOpen(true);
                      else if (value === 'manage-contracts') setManageContractsDialogOpen(true);
                      else if (value === 'manage-customers') setManageCustomersDialogOpen(true);
                      else if (value === 'bulk') setAddBulkRideDialogOpen(true);
                      setAddDropdownValue(undefined);
                    }}>
                      <SelectTrigger className="w-48 bg-white">
                        <Plus size={16} className="mr-2" />
                        <SelectValue placeholder="+ Add" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manage-contracts">Manage Contracts</SelectItem>
                        <SelectItem value="manage-customers">Manage Customers</SelectItem>
                        <SelectItem value="driver">Add Driver</SelectItem>
                        <SelectItem value="booking">Add Booking</SelectItem>
                        <SelectItem value="bulk">Add Bulk Ride</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select onValueChange={(value) => {
                      if (value === 'driver') setDriverReportDialogOpen(true);
                      else if (value === 'contract') setContractReportDialogOpen(true);
                      else if (value === 'ride') setRideReportDialogOpen(true);
                    }}>
                      <SelectTrigger className="w-48 bg-white">
                        <SelectValue placeholder="📊 Reports" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="driver">Driver Report</SelectItem>
                        <SelectItem value="contract">Contract Report</SelectItem>
                        <SelectItem value="ride">Ride Report</SelectItem>
                      </SelectContent>
                    </Select>

                    <Button 
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                      onClick={() => {
                        const completedBookings = bookings.filter(b => b.status === 'completed');
                        if (completedBookings.length === 0) {
                          toast.error('No completed bookings to generate invoice');
                        } else {
                          toast.success(`${completedBookings.length} invoices ready to generate`);
                        }
                      }}
                    >
                      <PoundSterling size={16} className="mr-2" />
                      Generate Invoice
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-bold">Data Management</CardTitle>
                <p className="text-sm text-gray-500">View and manage all bookings, contracts, and customers</p>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="bookings" className="space-y-4">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="bookings">Bookings</TabsTrigger>
                    <TabsTrigger value="contracts">Contracts (0)</TabsTrigger>
                    <TabsTrigger value="customers">Customers (0)</TabsTrigger>
                  </TabsList>

                  {/* Bookings Tab */}
                  <TabsContent value="bookings">
                    <div className="space-y-6">
                      {/* Invoice Statistics */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <p className="text-sm text-blue-600 font-semibold">Total Invoices</p>
                          <p className="text-3xl font-bold text-blue-800 mt-2">
                            {bookings.filter(b => b.status === 'completed').length}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-green-50 border-green-200">
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <p className="text-sm text-green-600 font-semibold">Paid Invoices</p>
                          <p className="text-3xl font-bold text-green-800 mt-2">
                            {bookings.filter(b => b.status === 'completed').length}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-blue-100 border-yellow-200">
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <p className="text-sm text-yellow-600 font-semibold">Pending Payment</p>
                          <p className="text-3xl font-bold text-yellow-800 mt-2">0</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-purple-50 border-purple-200">
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <p className="text-sm text-purple-600 font-semibold">Total Revenue</p>
                          <p className="text-3xl font-bold text-purple-800 mt-2">
                            £{bookings.filter(b => b.status === 'completed').reduce((sum, b) => sum + (b.price || 0), 0).toFixed(2)}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Invoices Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle>All Invoices</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="pricing-table">
                          <thead>
                            <tr>
                              <th>Invoice #</th>
                              <th>Customer Name</th>
                              <th>Booking Date</th>
                              <th>Route</th>
                              <th>Vehicle</th>
                              <th>Driver</th>
                              <th>Amount</th>
                              <th>Discount</th>
                              <th>Final Amount</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bookings.filter(b => b.status === 'completed').length === 0 ? (
                              <tr>
                                <td colSpan="11" className="text-center py-8 text-gray-500">
                                  No invoices yet. Completed bookings will appear here.
                                </td>
                              </tr>
                            ) : (
                              bookings.filter(b => b.status === 'completed').map((booking, index) => {
                                const driver = drivers.find(d => d.id === booking.driver_id);
                                const invoiceNumber = `INV-${new Date(booking.booking_date).getFullYear()}-${String(index + 1).padStart(4, '0')}`;
                                
                                return (
                                  <tr key={booking.id}>
                                    <td className="font-semibold text-blue-600">{invoiceNumber}</td>
                                    <td>{booking.customer_name || 'N/A'}</td>
                                    <td>{booking.booking_date}</td>
                                    <td className="text-sm">
                                      <div>{booking.pickup_location?.address?.substring(0, 30)}...</div>
                                      <div className="text-gray-500">to {booking.dropoff_location?.address?.substring(0, 30)}...</div>
                                    </td>
                                    <td className="capitalize">{booking.vehicle_type}</td>
                                    <td>{driver?.name || 'Unassigned'}</td>
                                    <td>£{booking.original_price ? booking.original_price.toFixed(2) : (booking.price || 0).toFixed(2)}</td>
                                    <td>
                                      {booking.discount_amount ? (
                                        <span className="text-red-600">-£{(booking.discount_amount || 0).toFixed(2)}</span>
                                      ) : (
                                        <span className="text-gray-400">£0.00</span>
                                      )}
                                    </td>
                                    <td className="font-bold text-green-600">£{(booking.price || 0).toFixed(2)}</td>
                                    <td>
                                      <Badge className="bg-green-600">Paid</Badge>
                                    </td>
                                    <td>
                                      <div className="flex gap-2">
                                        <Button
                                          onClick={() => {
                                            setSelectedInvoice({ ...booking, invoiceNumber, driver });
                                            setInvoiceDialogOpen(true);
                                          }}
                                          variant="outline"
                                          size="sm"
                                        >
                                          View
                                        </Button>
                                        <Button
                                          onClick={() => {
                                            // Download invoice logic
                                            window.print();
                                          }}
                                          variant="outline"
                                          size="sm"
                                          className="bg-blue-50"
                                        >
                                          Print
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                    </div>
                  </TabsContent>

                  {/* Contracts Tab */}
                  <TabsContent value="contracts">
                    <Card>
                      <CardContent className="pt-6">
                        {contracts.length === 0 ? (
                          <div className="text-center py-12">
                            <PoundSterling size={48} className="mx-auto mb-4 text-gray-400" />
                            <h3 className="text-xl font-semibold mb-2">No Contracts Yet</h3>
                            <p className="text-gray-500 mb-4">Create long-term booking contracts with customers</p>
                            <Button className="bg-purple-600 hover:bg-purple-700" onClick={() => setAddContractDialogOpen(true)}>
                              <Plus size={16} className="mr-2" />
                              Add First Contract
                            </Button>
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="pricing-table">
                              <thead>
                                <tr>
                                  <th>Company Name</th>
                                  <th>Contact Person</th>
                                  <th>Email</th>
                                  <th>Phone</th>
                                  <th>Contract Type</th>
                                  <th>Start Date</th>
                                  <th>End Date</th>
                                  <th>Monthly Rides</th>
                                  <th>Rate/Ride</th>
                                  <th>Total Amount</th>
                                  <th>Status</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {contracts.map((contract) => (
                                  <tr key={contract.id}>
                                    <td className="font-semibold">{contract.company_name}</td>
                                    <td>{contract.contact_person}</td>
                                    <td>{contract.email}</td>
                                    <td>{contract.phone}</td>
                                    <td className="capitalize">{contract.contract_type}</td>
                                    <td>{contract.start_date}</td>
                                    <td>{contract.end_date}</td>
                                    <td className="text-center">{contract.monthly_rides}</td>
                                    <td>£{(contract.rate_per_ride || 0).toFixed(2)}</td>
                                    <td className="font-bold text-green-600">£{(contract.total_amount || 0).toFixed(2)}</td>
                                    <td>
                                      <Badge className={contract.status === 'active' ? 'bg-green-600' : 'bg-gray-600'}>
                                        {contract.status}
                                      </Badge>
                                    </td>
                                    <td>
                                      <div className="flex gap-2">
                                        <Button variant="outline" size="sm">
                                          <Edit size={14} className="mr-1" />
                                          Edit
                                        </Button>
                                        <Button variant="outline" size="sm" className="text-red-600">
                                          <Trash2 size={14} className="mr-1" />
                                          Delete
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Customers Tab */}
                  <TabsContent value="customers">
                    <Card>
                      <CardContent className="pt-6">
                        {customers.length === 0 ? (
                          <div className="text-center py-12">
                            <User size={48} className="mx-auto mb-4 text-gray-400" />
                            <h3 className="text-xl font-semibold mb-2">No Customers Yet</h3>
                            <p className="text-gray-500 mb-4">Customer list will appear as bookings are created</p>
                            <Button className="bg-green-600 hover:bg-green-700" onClick={() => setAddCustomerDialogOpen(true)}>
                              <Plus size={16} className="mr-2" />
                              Add Customer Manually
                            </Button>
                            <div className="mt-6 p-4 bg-blue-50 rounded-lg max-w-md mx-auto">
                              <p className="text-sm text-blue-800">
                                💡 Customers are automatically added from bookings. You can also add them manually.
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="pricing-table">
                              <thead>
                                <tr>
                                  <th>Name</th>
                                  <th>Email</th>
                                  <th>Phone</th>
                                  <th>Address</th>
                                  <th>Total Bookings</th>
                                  <th>Total Spent</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {customers.map((customer, index) => (
                                  <tr key={index}>
                                    <td className="font-semibold">{customer.name}</td>
                                    <td>{customer.email}</td>
                                    <td>{customer.phone}</td>
                                    <td>{customer.address}</td>
                                    <td className="text-center">{(customer.total_bookings || 0)}</td>
                                    <td className="font-bold text-green-600">£{(customer.total_spent || 0).toFixed(2)}</td>
                                    <td>
                                      <div className="flex gap-1">
                                        <Button variant="outline" size="sm" onClick={() => {
                                          setEditingCustomer(customer);
                                          setEditCustomerDialogOpen(true);
                                        }}>
                                          <Edit size={14} />
                                        </Button>
                                        <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={async () => {
                                          if (window.confirm('Are you sure you want to delete this customer?')) {
                                            try {
                                              await axios.delete(`${API}/admin/customers/${customer.id}`);
                                              toast.success('Customer deleted successfully');
                                              fetchCustomers();
                                            } catch (error) {
                                              toast.error('Failed to delete customer');
                                            }
                                          }
                                        }}>
                                          <Trash2 size={14} />
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings size={20} />
                  Application Settings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-blue-50 p-6 rounded-lg text-center">
                  <Settings size={48} className="mx-auto mb-4 text-blue-600" />
                  <h3 className="text-xl font-semibold mb-2">Manage API Keys & Configurations</h3>
                  <p className="text-gray-600 mb-4">
                    Configure third-party integrations including Twilio, Email SMTP, Google Maps, and Payment Gateway settings.
                  </p>
                  <Button 
                    onClick={() => navigate('/admin/settings')}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Open Settings Panel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Assign Driver Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedBooking?.driver_id ? "Reassign Driver" : "Assign Driver"}</DialogTitle>
            <DialogDescription>
              {selectedBooking?.driver_id ? "Change the driver for this booking" : "Select a driver for this booking"}
            </DialogDescription>
          </DialogHeader>
          <div className="assign-dialog-content">
            {selectedBooking && (
              <div className="booking-summary">
                <p><strong>Customer:</strong> {selectedBooking.customer_name}</p>
                <p><strong>Route:</strong> {selectedBooking.pickup_location.address} → {selectedBooking.dropoff_location.address}</p>
                <p><strong>Vehicle:</strong> {selectedBooking.vehicle_type.toUpperCase()}</p>
                <p><strong>Date:</strong> {selectedBooking.booking_date} at {selectedBooking.booking_time}</p>
                {selectedBooking.driver_id && (
                  <div className="mt-3 p-2 bg-blue-50 rounded">
                    <p className="text-sm text-blue-800">
                      <strong>Current Driver:</strong> {drivers.find(d => d.id === selectedBooking.driver_id)?.name || "Unknown"}
                    </p>
                  </div>
                )}
              </div>
            )}
            <div className="space-y-2">
            <div className="space-y-2">
              <Label className="font-semibold">Driver Category</Label>
              <Select value={driverReportData.driver_category || "all"} onValueChange={(v) => setDriverReportData({...driverReportData, driver_category: v === "all" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="All Categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {driverCategories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-semibold">Driver Category</Label>
              <Select value={driverReportData.driver_category || "all"} onValueChange={(v) => setDriverReportData({...driverReportData, driver_category: v === "all" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="All Categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {driverCategories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

              <Label>Select Driver</Label>
              <Select value={selectedDriver} onValueChange={setSelectedDriver}>
                <SelectTrigger data-testid="driver-select">
                  <SelectValue placeholder="Select a driver" />
                </SelectTrigger>
                <SelectContent>
                  {drivers.filter(d => d.status === "available" || d.id === selectedBooking?.driver_id).map((driver) => (
                    <SelectItem key={driver.id} value={driver.id} data-testid={`driver-option-${driver.id}`}>
                      {driver.name || "Unknown"} - {driver.vehicle_type.toUpperCase()} ({driver.license_plate}) - {driver.status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                💡 Available drivers and the current driver are shown
              </p>
            </div>
            <Button onClick={handleAssignDriver} className="w-full" data-testid="confirm-assign-button">
              {selectedBooking?.driver_id ? "Reassign Driver" : "Assign Driver"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Driver Dialog */}
      <Dialog open={addDriverDialogOpen} onOpenChange={setAddDriverDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Driver</DialogTitle>
            <DialogDescription>
              Enter all driver information, documents, and bank details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Driver Category */}
            <div>
              <h3 className="text-lg font-semibold mb-3 border-b pb-2">Driver Category</h3>
              <div className="flex gap-3 items-end">
                <div className="space-y-2 flex-1">
                  <Label>Select Category</Label>
                  <Select value={newDriverData.driver_category} onValueChange={(v) => setNewDriverData({...newDriverData, driver_category: v})}>
                    <SelectTrigger><SelectValue placeholder="Select a category" /></SelectTrigger>
                    <SelectContent>
                      {driverCategories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowAddCategory(!showAddCategory)}>
                  {showAddCategory ? "Cancel" : "+ New Category"}
                </Button>
              </div>
              {showAddCategory && (
                <div className="flex gap-2 mt-3">
                  <Input placeholder="Enter new category name" value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleAddCategory()} />
                  <Button onClick={handleAddCategory}>Add</Button>
                </div>
              )}
            </div>

            {/* Profile Picture */}
            <div>
              <h3 className="text-lg font-semibold mb-3 border-b pb-2">Profile Picture</h3>
              <div className="p-3 bg-gray-50 rounded-lg">
                {newDriverData.profile_pic && <img src={newDriverData.profile_pic} alt="Profile" className="w-24 h-24 rounded-full object-cover mb-2 border-2 border-gray-200" />}
                <Input type="file" accept="image/*" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { setNewDriverData({...newDriverData, profile_pic: reader.result}); }; reader.readAsDataURL(file); } }} className="cursor-pointer" />
              </div>
            </div>

            {/* Personal Information */}
            <div>
              <h3 className="text-lg font-semibold mb-3 border-b pb-2">Personal Information</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="driver-name">Full Name *</Label>
                  <Input id="driver-name" placeholder="John Doe" value={newDriverData.name} onChange={(e) => setNewDriverData({...newDriverData, name: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="driver-email">Email *</Label>
                  <Input id="driver-email" type="email" placeholder="driver@example.com" value={newDriverData.email} onChange={(e) => setNewDriverData({...newDriverData, email: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="driver-phone">Phone *</Label>
                  <Input id="driver-phone" type="tel" placeholder="+44 7XXX XXXXXX" value={newDriverData.phone} onChange={(e) => setNewDriverData({...newDriverData, phone: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="driver-password">Password *</Label>
                  <Input id="driver-password" type="password" placeholder="Password" value={newDriverData.password} onChange={(e) => setNewDriverData({...newDriverData, password: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="driver-ni">NI Number</Label>
                  <Input id="driver-ni" placeholder="AB123456C" value={newDriverData.ni_number} onChange={(e) => setNewDriverData({...newDriverData, ni_number: e.target.value.toUpperCase()})} />
                </div>
                <div className="space-y-2"></div>
                <div className="space-y-2 col-span-3">
                  <Label htmlFor="driver-address">Address *</Label>
                  <Input id="driver-address" placeholder="123 Main Street, London" value={newDriverData.address} onChange={(e) => setNewDriverData({...newDriverData, address: e.target.value})} />
                </div>
              </div>
            </div>

            {/* Bank Details */}
            <div>
              <h3 className="text-lg font-semibold mb-3 border-b pb-2">Bank Details</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bank-name">Bank Name</Label>
                  <Input id="bank-name" placeholder="e.g., Barclays" value={newDriverData.bank_name} onChange={(e) => setNewDriverData({...newDriverData, bank_name: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bank-account">Account Number</Label>
                  <Input id="bank-account" placeholder="12345678" value={newDriverData.bank_account_number} onChange={(e) => setNewDriverData({...newDriverData, bank_account_number: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bank-sort">Sort Code</Label>
                  <Input id="bank-sort" placeholder="12-34-56" value={newDriverData.bank_sort_code} onChange={(e) => setNewDriverData({...newDriverData, bank_sort_code: e.target.value})} />
                </div>
              </div>
            </div>

            {/* Vehicle Information */}
            <div>
              <h3 className="text-lg font-semibold mb-3 border-b pb-2">Vehicle Information</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="car-number">Car Number *</Label>
                  <Input id="car-number" placeholder="ABC 123" value={newDriverData.license_plate} onChange={(e) => setNewDriverData({...newDriverData, license_plate: e.target.value.toUpperCase()})} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="car-make">Car Make</Label>
                  <Input id="car-make" placeholder="Toyota" value={newDriverData.car_make} onChange={(e) => setNewDriverData({...newDriverData, car_make: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="car-model">Car Model</Label>
                  <Input id="car-model" placeholder="Corolla" value={newDriverData.car_model} onChange={(e) => setNewDriverData({...newDriverData, car_model: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="car-colour">Car Colour</Label>
                  <Input id="car-colour" placeholder="Black" value={newDriverData.car_colour} onChange={(e) => setNewDriverData({...newDriverData, car_colour: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vehicle-type">Vehicle Type *</Label>
                  <Select value={newDriverData.vehicle_type} onValueChange={(value) => setNewDriverData({...newDriverData, vehicle_type: value})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="saloon">Saloon</SelectItem>
                      <SelectItem value="estate">Estate</SelectItem>
                      <SelectItem value="mvp">MVP</SelectItem>
                      <SelectItem value="luxury">Luxury</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max-seat">Max Seats</Label>
                  <Input id="max-seat" type="number" min="1" max="8" placeholder="4" value={newDriverData.max_seat} onChange={(e) => setNewDriverData({...newDriverData, max_seat: parseInt(e.target.value) || 4})} />
                </div>
              </div>
            </div>

            {/* License Documents */}
            <div>
              <h3 className="text-lg font-semibold mb-3 border-b pb-2">License Documents & Expiry Dates</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 p-3 bg-blue-50 rounded">
                  <div className="space-y-2">
                    <Label>DVLA Licence</Label>
                    <Input type="file" accept="image/*,application/pdf" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { setNewDriverData({...newDriverData, dvla_licence: reader.result}); }; reader.readAsDataURL(file); } }} className="cursor-pointer" />
                  </div>
                  <div className="space-y-2">
                    <Label>DVLA Expiry</Label>
                    <Input type="date" value={newDriverData.dvla_licence_expiry} onChange={(e) => setNewDriverData({...newDriverData, dvla_licence_expiry: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 p-3 bg-green-50 rounded">
                  <div className="space-y-2">
                    <Label>PHD Licence</Label>
                    <Input type="file" accept="image/*,application/pdf" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { setNewDriverData({...newDriverData, phd_licence: reader.result}); }; reader.readAsDataURL(file); } }} className="cursor-pointer" />
                  </div>
                  <div className="space-y-2">
                    <Label>PHD Expiry</Label>
                    <Input type="date" value={newDriverData.phd_licence_expiry} onChange={(e) => setNewDriverData({...newDriverData, phd_licence_expiry: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 p-3 bg-blue-100 rounded">
                  <div className="space-y-2">
                    <Label>PHV Licence</Label>
                    <Input type="file" accept="image/*,application/pdf" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { setNewDriverData({...newDriverData, phv_licence: reader.result}); }; reader.readAsDataURL(file); } }} className="cursor-pointer" />
                  </div>
                  <div className="space-y-2">
                    <Label>PHV Expiry</Label>
                    <Input type="date" value={newDriverData.phv_licence_expiry} onChange={(e) => setNewDriverData({...newDriverData, phv_licence_expiry: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 p-3 bg-purple-50 rounded">
                  <div className="space-y-2">
                    <Label>Insurance Certificate</Label>
                    <Input type="file" accept="image/*,application/pdf" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { setNewDriverData({...newDriverData, insurance_certificate: reader.result}); }; reader.readAsDataURL(file); } }} className="cursor-pointer" />
                  </div>
                  <div className="space-y-2">
                    <Label>Insurance Expiry</Label>
                    <Input type="date" value={newDriverData.insurance_certificate_expiry} onChange={(e) => setNewDriverData({...newDriverData, insurance_certificate_expiry: e.target.value})} />
                  </div>
                </div>
              </div>
                  <div style={{backgroundColor:"#dbeafe",border:"2px solid #93c5fd",borderRadius:"8px",padding:"12px"}} className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>MOT Certificate</Label>
                      {newDriverData.mot_certificate && <p className="text-xs text-green-600 font-bold">Document on file</p>}
                      <Input type="file" accept="image/*,application/pdf" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { setNewDriverData({...newDriverData, mot_certificate: reader.result}); }; reader.readAsDataURL(file); } }} className="cursor-pointer" />
                    </div>
                    <div className="space-y-2">
                      <Label>MOT Expiry</Label>
                      <Input type="date" value={newDriverData.mot_expiry} onChange={(e) => setNewDriverData({...newDriverData, mot_expiry: e.target.value})} />
                    </div>
                  </div>
              <p className="text-xs text-gray-500 mt-3">
                📄 Admin & driver will receive notifications 1 month and 15 days before any document expires.
              </p>
            </div>

            <Button onClick={handleAddDriver} className="w-full" size="lg">
              <Car size={16} className="mr-2" />
              Add Driver
            </Button>
          </div>

        </DialogContent>
      </Dialog>
      {/* View Driver Dialog */}
      <Dialog open={viewDriverDialogOpen} onOpenChange={setViewDriverDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Driver Details</DialogTitle>
          </DialogHeader>
          {viewingDriver && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-3 border-b pb-2">Profile Picture</h3>
                <div className="p-3 bg-gray-50 rounded-lg">
                  {viewingDriver.profile_pic ? (<img src={viewingDriver.profile_pic} alt="Profile" className="w-32 h-32 rounded-full object-cover border-2 border-gray-200" />) : (<p className="text-sm text-gray-400">No profile picture uploaded</p>)}
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-3 border-b pb-2">Personal Information</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div><p className="text-sm text-gray-500">Full Name</p><p className="font-medium">{viewingDriver.name}</p></div>
                  <div><p className="text-sm text-gray-500">Email</p><p className="font-medium">{viewingDriver.email || "N/A"}</p></div>
                  <div><p className="text-sm text-gray-500">Phone</p><p className="font-medium">{viewingDriver.phone}</p></div>
                  <div><p className="text-sm text-gray-500">NI Number</p><p className="font-medium">{viewingDriver.ni_number || "N/A"}</p></div>
                  <div><p className="text-sm text-gray-500">Driver Category</p><p className="font-medium">{viewingDriver.driver_category || "N/A"}</p></div>
                  <div><p className="text-sm text-gray-500">Driver Type</p><p className="font-medium">{viewingDriver.driver_type || "N/A"}</p></div>
                  <div className="col-span-3"><p className="text-sm text-gray-500">Address</p><p className="font-medium">{viewingDriver.address || "N/A"}</p></div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-3 border-b pb-2">Bank Details</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div><p className="text-sm text-gray-500">Bank Name</p><p className="font-medium">{viewingDriver.bank_name || "N/A"}</p></div>
                  <div><p className="text-sm text-gray-500">Account Number</p><p className="font-medium">{viewingDriver.bank_account_number || "N/A"}</p></div>
                  <div><p className="text-sm text-gray-500">Sort Code</p><p className="font-medium">{viewingDriver.bank_sort_code || "N/A"}</p></div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-3 border-b pb-2">Vehicle Information</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div><p className="text-sm text-gray-500">License Plate</p><p className="font-medium">{viewingDriver.license_plate || "N/A"}</p></div>
                  <div><p className="text-sm text-gray-500">Car Make</p><p className="font-medium">{viewingDriver.car_make || "N/A"}</p></div>
                  <div><p className="text-sm text-gray-500">Car Model</p><p className="font-medium">{viewingDriver.car_model || "N/A"}</p></div>
                  <div><p className="text-sm text-gray-500">Car Colour</p><p className="font-medium">{viewingDriver.car_colour || "N/A"}</p></div>
                  <div><p className="text-sm text-gray-500">Vehicle Type</p><p className="font-medium uppercase">{viewingDriver.vehicle_type || "N/A"}</p></div>
                  <div><p className="text-sm text-gray-500">Max Seats</p><p className="font-medium">{viewingDriver.max_seat || "N/A"}</p></div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-3 border-b pb-2">License Documents</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-blue-50 rounded-lg"><p className="font-semibold text-sm mb-1">DVLA Licence</p><p className="text-xs text-gray-500 mb-2">Expiry: {viewingDriver.dvla_licence_expiry || "N/A"}</p>{viewingDriver.dvla_licence ? (<img src={viewingDriver.dvla_licence} alt="DVLA" className="w-full max-h-64 rounded border mt-1 cursor-pointer" />) : (<p className="text-sm text-gray-400">No document</p>)}</div>
                  <div className="p-3 bg-green-50 rounded-lg"><p className="font-semibold text-sm mb-1">PHD Licence</p><p className="text-xs text-gray-500 mb-2">Expiry: {viewingDriver.phd_licence_expiry || "N/A"}</p>{viewingDriver.phd_licence ? (<img src={viewingDriver.phd_licence} alt="PHD" className="w-full max-h-64 rounded border mt-1 cursor-pointer" />) : (<p className="text-sm text-gray-400">No document</p>)}</div>
                  <div className="p-3 bg-blue-100 rounded-lg"><p className="font-semibold text-sm mb-1">PHV Licence</p><p className="text-xs text-gray-500 mb-2">Expiry: {viewingDriver.phv_licence_expiry || "N/A"}</p>{viewingDriver.phv_licence ? (<img src={viewingDriver.phv_licence} alt="PHV" className="w-full max-h-64 rounded border mt-1 cursor-pointer" />) : (<p className="text-sm text-gray-400">No document</p>)}</div>
                  <div className="p-3 bg-purple-50 rounded-lg"><p className="font-semibold text-sm mb-1">Insurance Certificate</p><p className="text-xs text-gray-500 mb-2">Expiry: {viewingDriver.insurance_certificate_expiry || "N/A"}</p>{viewingDriver.insurance_certificate ? (<img src={viewingDriver.insurance_certificate} alt="Insurance" className="w-full max-h-64 rounded border mt-1 cursor-pointer" />) : (<p className="text-sm text-gray-400">No document</p>)}</div>
                  <div className="p-3 bg-blue-50 rounded-lg"><p className="font-semibold text-sm mb-1">MOT Certificate</p><p className="text-xs text-gray-500 mb-2">Expiry: {viewingDriver.mot_expiry || "N/A"}</p>{viewingDriver.mot_certificate ? (<img src={viewingDriver.mot_certificate} alt="MOT" className="w-full max-h-64 rounded border mt-1 cursor-pointer" />) : (<p className="text-sm text-gray-400">No document</p>)}</div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => { setViewDriverDialogOpen(false); openEditDialog(viewingDriver); }} className="flex-1"><Edit size={14} className="mr-1" /> Edit Driver</Button>
                <Button variant="outline" onClick={() => setViewDriverDialogOpen(false)} className="flex-1">Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Driver Dialog */}
      <Dialog open={editDriverDialogOpen} onOpenChange={setEditDriverDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Driver</DialogTitle>
            <DialogDescription>
              Update driver information, documents, and bank details
            </DialogDescription>
          </DialogHeader>
          {editingDriver && (
            <div className="space-y-4">
              {/* Driver Category */}
              <div>
                <h3 className="text-lg font-semibold mb-3 border-b pb-2">Driver Category</h3>
                <div className="space-y-2">
                  <Label>Select Category</Label>
                  <Select value={editingDriver.driver_category || ""} onValueChange={(v) => setEditingDriver({...editingDriver, driver_category: v})}>
                    <SelectTrigger><SelectValue placeholder="Select a category" /></SelectTrigger>
                    <SelectContent>
                      {driverCategories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Profile Picture */}
              <div>
                <h3 className="text-lg font-semibold mb-3 border-b pb-2">Profile Picture</h3>
                <div className="p-3 bg-gray-50 rounded-lg">
                  {editingDriver.profile_pic && <img src={editingDriver.profile_pic} alt="Profile" className="w-24 h-24 rounded-full object-cover mb-2 border-2 border-gray-200" />}
                  <Input type="file" accept="image/*" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { setEditingDriver({...editingDriver, profile_pic: reader.result}); }; reader.readAsDataURL(file); } }} className="cursor-pointer" />
                </div>
              </div>

              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold mb-3 border-b pb-2">Personal Information</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Full Name *</Label>
                    <Input placeholder="John Doe" value={editingDriver.name} onChange={(e) => setEditingDriver({...editingDriver, name: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input type="email" placeholder="driver@example.com" value={editingDriver.email} onChange={(e) => setEditingDriver({...editingDriver, email: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone *</Label>
                    <Input type="tel" placeholder="+44 7XXX XXXXXX" value={editingDriver.phone} onChange={(e) => setEditingDriver({...editingDriver, phone: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>NI Number</Label>
                    <Input placeholder="AB123456C" value={editingDriver.ni_number} onChange={(e) => setEditingDriver({...editingDriver, ni_number: e.target.value.toUpperCase()})} />
                  </div>
                  <div className="space-y-2"></div>
                  <div className="space-y-2"></div>
                  <div className="space-y-2 col-span-3">
                    <Label>Address *</Label>
                    <Input placeholder="123 Main Street, London" value={editingDriver.address} onChange={(e) => setEditingDriver({...editingDriver, address: e.target.value})} />
                  </div>
                </div>
              </div>

              {/* Bank Details */}
              <div>
                <h3 className="text-lg font-semibold mb-3 border-b pb-2">Bank Details</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Bank Name</Label>
                    <Input placeholder="e.g., Barclays" value={editingDriver.bank_name} onChange={(e) => setEditingDriver({...editingDriver, bank_name: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Account Number</Label>
                    <Input placeholder="12345678" value={editingDriver.bank_account_number} onChange={(e) => setEditingDriver({...editingDriver, bank_account_number: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Sort Code</Label>
                    <Input placeholder="12-34-56" value={editingDriver.bank_sort_code} onChange={(e) => setEditingDriver({...editingDriver, bank_sort_code: e.target.value})} />
                  </div>
                </div>
              </div>

              {/* Vehicle Information */}
              <div>
                <h3 className="text-lg font-semibold mb-3 border-b pb-2">Vehicle Information</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Car Number *</Label>
                    <Input placeholder="ABC 123" value={editingDriver.license_plate} onChange={(e) => setEditingDriver({...editingDriver, license_plate: e.target.value.toUpperCase()})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Car Make</Label>
                    <Input placeholder="Toyota" value={editingDriver.car_make} onChange={(e) => setEditingDriver({...editingDriver, car_make: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Car Model</Label>
                    <Input placeholder="Corolla" value={editingDriver.car_model} onChange={(e) => setEditingDriver({...editingDriver, car_model: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Car Colour</Label>
                    <Input placeholder="Black" value={editingDriver.car_colour} onChange={(e) => setEditingDriver({...editingDriver, car_colour: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Vehicle Type *</Label>
                    <Select value={editingDriver.vehicle_type} onValueChange={(v) => setEditingDriver({...editingDriver, vehicle_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="saloon">Saloon</SelectItem>
                        <SelectItem value="estate">Estate</SelectItem>
                        <SelectItem value="mvp">MVP</SelectItem>
                        <SelectItem value="luxury">Luxury</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Max Seats</Label>
                    <Input type="number" min="1" max="8" placeholder="4" value={editingDriver.max_seat} onChange={(e) => setEditingDriver({...editingDriver, max_seat: parseInt(e.target.value) || 4})} />
                  </div>
                </div>
              </div>

              {/* License Documents */}
              <div>
                <h3 className="text-lg font-semibold mb-3 border-b pb-2">License Documents & Expiry Dates</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 p-3 bg-blue-50 rounded">
                    <div className="space-y-2">
                      <Label>DVLA Licence</Label>
                      {editingDriver.dvla_licence && <p className="text-xs text-green-600">Document on file</p>}
                      <Input type="file" accept="image/*,application/pdf" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { setEditingDriver({...editingDriver, dvla_licence: reader.result}); }; reader.readAsDataURL(file); } }} className="cursor-pointer" />
                    </div>
                    <div className="space-y-2">
                      <Label>DVLA Expiry</Label>
                      <Input type="date" value={editingDriver.dvla_licence_expiry} onChange={(e) => setEditingDriver({...editingDriver, dvla_licence_expiry: e.target.value})} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 p-3 bg-green-50 rounded">
                    <div className="space-y-2">
                      <Label>PHD Licence</Label>
                      {editingDriver.phd_licence && <p className="text-xs text-green-600">Document on file</p>}
                      <Input type="file" accept="image/*,application/pdf" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { setEditingDriver({...editingDriver, phd_licence: reader.result}); }; reader.readAsDataURL(file); } }} className="cursor-pointer" />
                    </div>
                    <div className="space-y-2">
                      <Label>PHD Expiry</Label>
                      <Input type="date" value={editingDriver.phd_licence_expiry} onChange={(e) => setEditingDriver({...editingDriver, phd_licence_expiry: e.target.value})} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 p-3 bg-purple-50 rounded">
                    <div className="space-y-2">
                      <Label>Insurance Certificate</Label>
                      {editingDriver.insurance_certificate && <p className="text-xs text-green-600">Document on file</p>}
                      <Input type="file" accept="image/*,application/pdf" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { setEditingDriver({...editingDriver, insurance_certificate: reader.result}); }; reader.readAsDataURL(file); } }} className="cursor-pointer" />
                    </div>
                    <div className="space-y-2">
                      <Label>Insurance Expiry</Label>
                      <Input type="date" value={editingDriver.insurance_certificate_expiry} onChange={(e) => setEditingDriver({...editingDriver, insurance_certificate_expiry: e.target.value})} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 p-3 bg-blue-100 rounded">
                    <div className="space-y-2">
                      <Label>PHV Licence</Label>
                      {editingDriver?.phv_licence && <p className="text-xs text-green-600">Document on file</p>}
                      <Input type="file" accept="image/*,application/pdf" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { setEditingDriver({...editingDriver, phv_licence: reader.result}); }; reader.readAsDataURL(file); } }} className="cursor-pointer" />
                    </div>
                    <div className="space-y-2">
                      <Label>PHV Expiry</Label>
                      <Input type="date" value={editingDriver?.phv_licence_expiry} onChange={(e) => setEditingDriver({...editingDriver, phv_licence_expiry: e.target.value})} />
                    </div>
                  </div>
                  <div style={{backgroundColor:"#dbeafe",border:"2px solid #93c5fd",borderRadius:"8px",padding:"12px"}} className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>MOT Certificate</Label>
                      {editingDriver.mot_certificate && <p className="text-xs text-green-600 font-bold">Document on file</p>}
                      <Input type="file" accept="image/*,application/pdf" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { setEditingDriver({...editingDriver, mot_certificate: reader.result}); }; reader.readAsDataURL(file); } }} className="cursor-pointer" />
                    </div>
                    <div className="space-y-2">
                      <Label>MOT Expiry</Label>
                      <Input type="date" value={editingDriver.mot_expiry} onChange={(e) => setEditingDriver({...editingDriver, mot_expiry: e.target.value})} />
                    </div>
                  </div>
                  </div>
                </div>

              <Button onClick={handleEditDriver} className="w-full" size="lg">
                <Edit size={16} className="mr-2" />
                Update Driver
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Driver</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this driver? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {driverToDelete && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p><strong>Name:</strong> {driverToDelete.name || "Unknown"}</p>
                <p><strong>Phone:</strong> {driverToDelete.phone || "N/A"}</p>
                <p><strong>Vehicle:</strong> {driverToDelete.vehicle_type?.toUpperCase()} ({driverToDelete.license_plate})</p>
                <p><strong>Status:</strong> <Badge className={driverToDelete.status === "available" ? "bg-green-500" : "bg-orange-500"}>{driverToDelete.status}</Badge></p>
              </div>
              
              {driverToDelete.status === "busy" && (
                <div className="bg-blue-100 border border-yellow-200 p-3 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    ⚠️ <strong>Note:</strong> This driver is currently busy with active bookings. 
                    You must complete or reassign their bookings before deletion.
                  </p>
                </div>
              )}
              
              {driverToDelete.status === "available" && (
                <div className="bg-green-50 border border-green-200 p-3 rounded-lg">
                  <p className="text-sm text-green-800">
                    ✅ This driver is available and has no active bookings. Safe to delete.
                  </p>
                </div>
              )}
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setDeleteDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleDeleteDriver}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  <Trash2 size={16} className="mr-2" />
                  Delete Driver
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Base Pricing Dialog */}
      <Dialog open={editPricingDialogOpen} onOpenChange={setEditPricingDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Base Pricing</DialogTitle>
            <DialogDescription>
              Update pricing for {editingPricing?.vehicle_type?.toUpperCase()}
            </DialogDescription>
          </DialogHeader>
          {editingPricing && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="base-fare">Base Fare (First 10 Miles) *</Label>
                <Input
                  id="base-fare"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="37.00"
                  value={editingPricing.base_fare_10_miles}
                  onChange={(e) => setEditingPricing({...editingPricing, base_fare_10_miles: parseFloat(e.target.value) || 0})}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="per-mile">Per Mile (After 10 Miles) *</Label>
                <Input
                  id="per-mile"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="4.00"
                  value={editingPricing.per_mile_after_10}
                  onChange={(e) => setEditingPricing({...editingPricing, per_mile_after_10: parseFloat(e.target.value) || 0})}
                />
              </div>

              <Button onClick={handleUpdateBasePricing} className="w-full">
                <Edit size={16} className="mr-2" />
                Update Pricing
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Zipcode Pricing Dialog */}
      <Dialog open={addZipcodeDialogOpen} onOpenChange={setAddZipcodeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Zipcode Pricing</DialogTitle>
            <DialogDescription>
              Configure custom pricing for a specific zipcode
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="zipcode">Zipcode *</Label>
              <Input
                id="zipcode"
                placeholder="e.g., SW1A 1AA"
                value={newZipcodePricing.zipcode}
                onChange={(e) => setNewZipcodePricing({...newZipcodePricing, zipcode: e.target.value.toUpperCase()})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="zipcode-vehicle-type">Vehicle Type *</Label>
              <Select 
                value={newZipcodePricing.vehicle_type} 
                onValueChange={(value) => setNewZipcodePricing({...newZipcodePricing, vehicle_type: value})}
              >
                <SelectTrigger id="zipcode-vehicle-type">
                  <SelectValue placeholder="Select vehicle type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="saloon">Saloon</SelectItem>
                  <SelectItem value="estate">Estate</SelectItem>
                  <SelectItem value="mvp">MVP</SelectItem>
                  <SelectItem value="luxury">Luxury</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="zipcode-base-fare">Base Fare (First 10 Miles) *</Label>
              <Input
                id="zipcode-base-fare"
                type="number"
                step="0.01"
                min="0"
                placeholder="37.00"
                value={newZipcodePricing.base_fare_10_miles}
                onChange={(e) => setNewZipcodePricing({...newZipcodePricing, base_fare_10_miles: parseFloat(e.target.value) || 0})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="zipcode-per-mile">Per Mile (After 10 Miles) *</Label>
              <Input
                id="zipcode-per-mile"
                type="number"
                step="0.01"
                min="0"
                placeholder="4.00"
                value={newZipcodePricing.per_mile_after_10}
                onChange={(e) => setNewZipcodePricing({...newZipcodePricing, per_mile_after_10: parseFloat(e.target.value) || 0})}
              />
            </div>

            <Button onClick={handleAddZipcodePricing} className="w-full">
              <PoundSterling size={16} className="mr-2" />
              Add Zipcode Pricing
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Discount Code Dialog */}
      <Dialog open={addDiscountDialogOpen} onOpenChange={setAddDiscountDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Discount Code</DialogTitle>
            <DialogDescription>Create a new discount code for bookings</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="discount-code">Discount Code *</Label>
              <Input
                id="discount-code"
                placeholder="SUMMER2025"
                value={newDiscount.code}
                onChange={(e) => setNewDiscount({...newDiscount, code: e.target.value.toUpperCase()})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="discount-type">Discount Type *</Label>
              <Select 
                value={newDiscount.type} 
                onValueChange={(value) => setNewDiscount({...newDiscount, type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage (%)</SelectItem>
                  <SelectItem value="amount">Fixed Amount (£)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="discount-value">Value *</Label>
              <Input
                id="discount-value"
                type="number"
                step="0.01"
                min="0"
                placeholder={newDiscount.type === 'percentage' ? "10" : "5.00"}
                value={newDiscount.value}
                onChange={(e) => setNewDiscount({...newDiscount, value: parseFloat(e.target.value) || 0})}
              />
              <p className="text-xs text-gray-500">
                {newDiscount.type === 'percentage' ? 'Enter percentage (e.g., 10 for 10%)' : 'Enter amount in pounds (e.g., 5.00 for £5)'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start-date">Start Date *</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={newDiscount.start_date}
                  onChange={(e) => setNewDiscount({...newDiscount, start_date: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="end-date">End Date *</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={newDiscount.end_date}
                  onChange={(e) => setNewDiscount({...newDiscount, end_date: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="max-uses">Max Uses Per User *</Label>
              <Input
                id="max-uses"
                type="number"
                min="1"
                value={newDiscount.max_uses_per_user}
                onChange={(e) => setNewDiscount({...newDiscount, max_uses_per_user: parseInt(e.target.value) || 1})}
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is-active"
                checked={newDiscount.is_active}
                onChange={(e) => setNewDiscount({...newDiscount, is_active: e.target.checked})}
                className="rounded"
              />
              <Label htmlFor="is-active">Active</Label>
            </div>

            <Button onClick={handleAddDiscount} className="w-full">
              <Tag size={16} className="mr-2" />
              Create Discount Code
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Discount Code Dialog */}
      <Dialog open={editDiscountDialogOpen} onOpenChange={setEditDiscountDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Discount Code</DialogTitle>
            <DialogDescription>Update discount code details</DialogDescription>
          </DialogHeader>
          {editingDiscount && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-discount-code">Discount Code *</Label>
                <Input
                  id="edit-discount-code"
                  placeholder="SUMMER2025"
                  value={editingDiscount.code}
                  onChange={(e) => setEditingDiscount({...editingDiscount, code: e.target.value.toUpperCase()})}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-discount-type">Discount Type *</Label>
                <Select 
                  value={editingDiscount.type} 
                  onValueChange={(value) => setEditingDiscount({...editingDiscount, type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="amount">Fixed Amount (£)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-discount-value">Value *</Label>
                <Input
                  id="edit-discount-value"
                  type="number"
                  step="0.01"
                  min="0"
                  value={editingDiscount.value}
                  onChange={(e) => setEditingDiscount({...editingDiscount, value: parseFloat(e.target.value) || 0})}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-start-date">Start Date *</Label>
                  <Input
                    id="edit-start-date"
                    type="date"
                    value={editingDiscount.start_date}
                    onChange={(e) => setEditingDiscount({...editingDiscount, start_date: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-end-date">End Date *</Label>
                  <Input
                    id="edit-end-date"
                    type="date"
                    value={editingDiscount.end_date}
                    onChange={(e) => setEditingDiscount({...editingDiscount, end_date: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-max-uses">Max Uses Per User *</Label>
                <Input
                  id="edit-max-uses"
                  type="number"
                  min="1"
                  value={editingDiscount.max_uses_per_user}
                  onChange={(e) => setEditingDiscount({...editingDiscount, max_uses_per_user: parseInt(e.target.value) || 1})}
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="edit-is-active"
                  checked={editingDiscount.is_active}
                  onChange={(e) => setEditingDiscount({...editingDiscount, is_active: e.target.checked})}
                  className="rounded"
                />
                <Label htmlFor="edit-is-active">Active</Label>
              </div>

              <Button onClick={handleUpdateDiscount} className="w-full">
                <Tag size={16} className="mr-2" />
                Update Discount Code
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={editCustomerDialogOpen} onOpenChange={setEditCustomerDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
          </DialogHeader>
          {editingCustomer && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input value={editingCustomer.name || ''} onChange={(e) => setEditingCustomer({...editingCustomer, name: e.target.value})}/>
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input type="email" value={editingCustomer.email || ''} onChange={(e) => setEditingCustomer({...editingCustomer, email: e.target.value})}/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone *</Label>
                  <Input value={editingCustomer.phone || ''} onChange={(e) => setEditingCustomer({...editingCustomer, phone: e.target.value})}/>
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input value={editingCustomer.address || ''} onChange={(e) => setEditingCustomer({...editingCustomer, address: e.target.value})}/>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setEditCustomerDialogOpen(false)}>Cancel</Button>
                <Button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" onClick={async () => {
                  try {
                    await axios.put(`${API}/admin/customers/${editingCustomer.id}`, editingCustomer);
                    toast.success('Customer updated successfully');
                    setEditCustomerDialogOpen(false);
                    fetchCustomers();
                  } catch (error) {
                    toast.error('Failed to update customer');
                  }
                }}>Save Changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      
      {/* View Customer Dialog */}
      <Dialog open={viewCustomerDialogOpen} onOpenChange={setViewCustomerDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Customer Details</DialogTitle>
          </DialogHeader>
          {viewingCustomer && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <p><strong>Name:</strong> {viewingCustomer.name || '-'}</p>
                <p><strong>Email:</strong> {viewingCustomer.email || '-'}</p>
                <p><strong>Phone:</strong> {viewingCustomer.phone || '-'}</p>
                <p><strong>Address:</strong> {viewingCustomer.address || '-'}</p>
                <p><strong>Company:</strong> {viewingCustomer.company || '-'}</p>
                <p><strong>Notes:</strong> {viewingCustomer.notes || '-'}</p>
              </div>
              <Button variant="outline" onClick={() => setViewCustomerDialogOpen(false)} className="w-full">Close</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Customer Dialog */}
      <Dialog open={addCustomerDialogOpen} onOpenChange={setAddCustomerDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Customer</DialogTitle>
            <DialogDescription>Add a customer to your database</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customer-name">Customer Name *</Label>
                <Input
                  id="customer-name"
                  placeholder="John Doe"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({...newCustomer, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer-email">Email (Optional)</Label>
                <Input
                  id="customer-email"
                  type="email"
                  placeholder="john@example.com"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({...newCustomer, email: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer-phone">Phone (Optional)</Label>
                <Input
                  id="customer-phone"
                  type="tel"
                  placeholder="+44 7XXX XXXXXX"
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({...newCustomer, phone: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer-address">Address (Optional)</Label>
                <Input
                  id="customer-address"
                  placeholder="123 Main Street, London"
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer({...newCustomer, address: e.target.value})}
                />
              </div>
            </div>
            <Button onClick={handleAddCustomer} className="w-full" size="lg">
              <User size={16} className="mr-2" />
              Add Customer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Bulk Ride Dialog */}
      <Dialog open={addBulkRideDialogOpen} onOpenChange={setAddBulkRideDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Bulk Ride</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Row 1: Select Contract */}
            <div className="space-y-2">
              <Label>Select Contract *</Label>
              <Select value={bulkRideData.contract_id} onValueChange={(value) => {
                const selectedContract = contracts.find(c => c.id === value);
                const selectedCustomer = customers.find(cust => cust.id === selectedContract?.customer_id);
                setBulkRideData({
                  ...bulkRideData,
                  contract_id: value,
                  customer_id: selectedContract?.customer_id || '',
                  customer_name: selectedCustomer?.name || '',
                  service_type: selectedContract?.service_type || 'airport_transfer',
                  car_type: selectedContract?.car_type || 'saloon',
                  max_passengers: String(selectedContract?.max_passengers || '4'),
                  total_miles: String(selectedContract?.total_miles || ''),
                  total_fare: String(selectedContract?.total_fare || ''),
                  pickup_location: selectedContract?.pickup_location || '',
                  dropoff_location: selectedContract?.drop_location || selectedContract?.dropoff_location || '',
                  pickup_time: selectedContract?.pickup_time || '',
                  dropoff_time: selectedContract?.drop_time || '',
                  ride_type: selectedContract?.ride_type || 'one_way'
                });
              }}>
                <SelectTrigger><SelectValue placeholder="--Select Contract--" /></SelectTrigger>
                <SelectContent>
                  {contracts.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.contract_title || c.customer_name || c.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Row 2: Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Entry From Date *</Label>
                <Input type="date" value={bulkRideData.entry_from_date} onChange={(e) => setBulkRideData({...bulkRideData, entry_from_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Entry To Date *</Label>
                <Input type="date" value={bulkRideData.entry_to_date} onChange={(e) => setBulkRideData({...bulkRideData, entry_to_date: e.target.value})} />
              </div>
            </div>

            {/* Row 3: Select Driver */}
            <div className="space-y-2">
              <Label>Select Driver (Optional)</Label>
              <Select value={bulkRideData.driver_id} onValueChange={(value) => setBulkRideData({...bulkRideData, driver_id: value === 'none' ? '' : value})}>
                <SelectTrigger><SelectValue placeholder="--Select Driver--" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Driver</SelectItem>
                  {drivers.filter(d => !driverCategoryFilter || d.driver_category === driverCategoryFilter).map((driver) => (
                    <SelectItem key={driver.id} value={driver.id}>{driver.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Auto-filled Details Preview */}
            {bulkRideData.contract_id && (
              <div className="p-4 bg-gray-50 rounded-lg border">
                <h4 className="font-semibold mb-2 text-gray-700">Contract Details (Auto-filled)</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p><strong>Customer:</strong> {bulkRideData.customer_name || '-'}</p>
                  <p><strong>Service Type:</strong> {bulkRideData.service_type === 'airport_transfer' ? 'Airport Transfer' : 'Regular Transport'}</p>
                  <p><strong>Pickup:</strong> {bulkRideData.pickup_location || '-'}</p>
                  <p><strong>Drop:</strong> {bulkRideData.drop_location || '-'}</p>
                  <p><strong>Pickup Time:</strong> {bulkRideData.pickup_time || '-'}</p>
                  <p><strong>Drop Time:</strong> {bulkRideData.dropoff_time || '-'}</p>
                  <p><strong>Car Type:</strong> {bulkRideData.car_type || '-'}</p>
                  <p><strong>Fare:</strong> £{bulkRideData.total_fare || '0'}</p>
                </div>
              </div>
            )}

            {/* Note */}
            <div className="p-3 bg-blue-100 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> No SMS or email notifications will be sent for bulk ride bookings.
              </p>
            </div>

            {/* Submit Button */}
            <Button onClick={handleAddBulkRide} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
              Create Bulk Booking
            </Button>
          </div>
        </DialogContent>
      </Dialog>{/* Add Bulk Ride Dialog */}
      <Dialog open={addBulkRideDialogOpen} onOpenChange={setAddBulkRideDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Bulk Ride</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Row 1: Contract & Entry From Date */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Select Contract *</Label>
                <Select value={bulkRideData.contract_id} onValueChange={(value) => {
            const selectedContract = contracts.find(c => c.id === value);
            const selectedCustomer = customers.find(cust => cust.id === selectedContract?.customer_id);
            setBulkRideData({
              ...bulkRideData,
              contract_id: value,
              customer_id: selectedContract?.customer_id || '',
              customer_name: selectedCustomer?.name || selectedContract?.contact_person || '',
              service_type: selectedContract?.service_type || 'airport_transfer',
              car_type: selectedContract?.car_type || 'saloon',
              max_passengers: String(selectedContract?.max_passengers || '4'),
              total_miles: String(selectedContract?.total_miles || selectedContract?.rate_per_ride || ''),
              total_fare: String(selectedContract?.total_fare || selectedContract?.rate_per_ride || ''),
              pickup_location: selectedContract?.pickup_location || '',
              dropoff_location: selectedContract?.drop_location || selectedContract?.dropoff_location || '',
              pickup_time: selectedContract?.pickup_time || '',
              dropoff_time: selectedContract?.drop_time || '',
              ride_type: selectedContract?.ride_type || 'one_way'
            });
          }}>>
                  <SelectTrigger><SelectValue placeholder="--Select Contract--" /></SelectTrigger>
                  <SelectContent>
                    {contracts.map((c) => (<SelectItem key={c.id} value={c.id}>{c.contract_title || c.customer_name || c.id}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Entry From Date * (DD/MM/YYYY)</Label>
                <Input type="date" value={bulkRideData.entry_from_date} onChange={(e) => setBulkRideData({...bulkRideData, entry_from_date: e.target.value})}/>
              </div>
            </div>

            {/* Row 2: Entry Till Date & Driver */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Entry Till Date * (DD/MM/YYYY)</Label>
                <Input type="date" value={bulkRideData.entry_till_date} onChange={(e) => setBulkRideData({...bulkRideData, entry_till_date: e.target.value})}/>
              </div>
              <div className="space-y-2">
                <Label>Select Driver *</Label>
                <Select value={bulkRideData.driver_id} onValueChange={(value) => setBulkRideData({...bulkRideData, driver_id: value})}>
                  <SelectTrigger><SelectValue placeholder="--Select Driver--" /></SelectTrigger>
                  <SelectContent>
                    {drivers.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 3: Customer & Ride Type */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Select Customer Name *</Label>
                <Select value={bulkRideData.customer_id} onValueChange={(value) => {const c = customers.find(x => x.id === value); setBulkRideData({...bulkRideData, customer_id: value, customer_name: c?.name || ''});}}>
                  <SelectTrigger><SelectValue placeholder="--Select Customer--" /></SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Select Ride Type *</Label>
                <Select value={bulkRideData.ride_type} onValueChange={(value) => setBulkRideData({...bulkRideData, ride_type: value})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_way">One Way</SelectItem>
                    <SelectItem value="round_trip">Round Trip</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 4: Service Type & Car Type */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Select Service Type *</Label>
                <Select value={bulkRideData.service_type} onValueChange={(value) => setBulkRideData({...bulkRideData, service_type: value})}>
                  <SelectTrigger><SelectValue placeholder="--Select Service Type--" /></SelectTrigger>
                  <SelectContent>
                        <SelectItem value="airport_transfer">Airport Transfer</SelectItem>
                        <SelectItem value="regular_transport">Regular Transport</SelectItem>
                      </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Select Type of Car *</Label>
                <Select value={bulkRideData.car_type} onValueChange={(value) => setBulkRideData({...bulkRideData, car_type: value})}>
                  <SelectTrigger><SelectValue placeholder="--Select Vehicle Type--" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="saloon">Saloon</SelectItem>
                    <SelectItem value="estate">Estate</SelectItem>
                    <SelectItem value="mvp">MVP</SelectItem>
                    <SelectItem value="luxury">Luxury</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 5: Max Passengers & Total Miles */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Max. Number of Passengers *</Label>
                <Input type="number" placeholder="" value={bulkRideData.max_passengers} onChange={(e) => setBulkRideData({...bulkRideData, max_passengers: e.target.value})}/>
              </div>
              <div className="space-y-2">
                <Label>Total Miles *</Label>
                <Input type="number" placeholder="" value={bulkRideData.total_miles} onChange={(e) => setBulkRideData({...bulkRideData, total_miles: e.target.value})}/>
              </div>
            </div>

            {/* Row 6: Total Fare & Pickup Location */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Total Fare *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                  <Input type="number" step="0.01" className="pl-7" placeholder="" value={bulkRideData.total_fare} onChange={(e) => setBulkRideData({...bulkRideData, total_fare: e.target.value})}/>
                </div>
              </div>
              <div className="space-y-2">
                <Label>PickUp Location *</Label>
                <Input placeholder="" value={bulkRideData.pickup_location} onChange={(e) => setBulkRideData({...bulkRideData, pickup_location: e.target.value})}/>
              </div>
            </div>

            {/* Row 7: Drop Off Location & Pick Up Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Drop Off Location *</Label>
                <Input placeholder="" value={bulkRideData.dropoff_location} onChange={(e) => setBulkRideData({...bulkRideData, dropoff_location: e.target.value})}/>
              </div>
              <div className="space-y-2">
                <Label>Pick Up Time *</Label>
                <Input type="time" value={bulkRideData.pickup_time} onChange={(e) => setBulkRideData({...bulkRideData, pickup_time: e.target.value})}/>
              </div>
            </div>

            {/* Row 8: Drop Off Time & Payment Mode */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Drop Off Time *</Label>
                <Input type="time" value={bulkRideData.dropoff_time} onChange={(e) => setBulkRideData({...bulkRideData, dropoff_time: e.target.value})}/>
              </div>
              <div className="space-y-2">
                <Label>Payment Mode *</Label>
                <Select value={bulkRideData.payment_mode} onValueChange={(value) => setBulkRideData({...bulkRideData, payment_mode: value})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contract_billing">Contract Billing</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-6 items-center py-3 px-2 bg-gray-50 rounded-lg">
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="w-4 h-4" /><span className="text-sm font-medium">Send Email</span></label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} className="w-4 h-4" /><span className="text-sm font-medium">Send SMS</span></label>
            </div>
            {/* Submit Button */}
            <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-lg" onClick={async () => {
              if (!bulkRideData.contract_id) { toast.error('Please select a contract'); return; }
              if (!bulkRideData.entry_from_date || !bulkRideData.entry_till_date) { toast.error('Please select date range'); return; }
              if (!bulkRideData.pickup_location || !bulkRideData.dropoff_location) { toast.error('Please enter locations'); return; }
              if (!bulkRideData.total_fare) { toast.error('Please enter total fare'); return; }
              
              try {
                // Calculate number of days between dates
                const fromDate = new Date(bulkRideData.entry_from_date);
                const tillDate = new Date(bulkRideData.entry_till_date);
                const days = Math.ceil((tillDate - fromDate) / (1000 * 60 * 60 * 24)) + 1;
                
                // Create bookings for each day
                let successCount = 0;
                for (let i = 0; i < days; i++) {
                  const bookingDate = new Date(fromDate);
                  bookingDate.setDate(bookingDate.getDate() + i);
                  const dateStr = bookingDate.toISOString().split('T')[0];
                  
                  const response = await axios.post(`${API}/admin/bulk-bookings`, {
            contract_id: bulkRideData.contract_id,
            is_bulk_booking: true,
                    customer_name: bulkRideData.customer_name || customers.find(c => c.id === bulkRideData.customer_id)?.name || 'Bulk Booking',
                    customer_phone: customers.find(c => c.id === bulkRideData.customer_id)?.phone || '',
                    customer_email: customers.find(c => c.id === bulkRideData.customer_id)?.email || '',
                    pickup_location: { address: bulkRideData.pickup_location, lat: 51.5074, lng: -0.1278 },
                    dropoff_location: { address: bulkRideData.dropoff_location, lat: 51.5074, lng: -0.1278 },
                    vehicle_type: bulkRideData.car_type || 'saloon',
                    trip_type: bulkRideData.ride_type,
                    booking_date: dateStr,
                    booking_time: bulkRideData.pickup_time || '09:00',
                dropoff_time: bulkRideData.dropoff_time || '',
                    price: parseFloat(bulkRideData.total_fare),
                driver_id: bulkRideData.driver_id || ""
                  });
                  if (response.data.success) successCount++;
                }
                
                toast.success(`${successCount} bulk ride bookings created!`);
                setAddBulkRideDialogOpen(false);
                fetchBookings();
                // Reset form
                setBulkRideData({
                  contract_id: "", entry_from_date: "", entry_till_date: "", driver_id: "",
                  customer_id: "", customer_name: "", ride_type: "one_way", service_type: "",
                  car_type: "", max_passengers: "", total_miles: "", total_fare: "",
                  pickup_location: "", dropoff_location: "", pickup_time: "", dropoff_time: "",
                  payment_mode: "contract_billing"
                });
              } catch (error) { toast.error('Failed: ' + (error.response?.data?.detail || error.message)); }
            }}>Create Bulk Ride Bookings</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Booking Dialog */}
      <Dialog open={bookingListDialogOpen} onOpenChange={setBookingListDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex justify-between items-center">
              <DialogTitle className="text-xl font-bold">Manage Ride Booking</DialogTitle>
              <Button className="bg-gray-800 hover:bg-gray-900 text-white" onClick={() => { setBookingListDialogOpen(false); setAddBookingDialogOpen(true); }}>+ Add New Booking</Button>
            </div>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2 mb-4">
              <Button variant="outline" size="sm">Copy</Button>
              <Button variant="outline" size="sm">CSV</Button>
              <Button variant="outline" size="sm">Excel</Button>
              <Button variant="outline" size="sm">Print</Button>
              <div className="ml-auto"><Input placeholder="Search:" className="w-48" /></div>
            </div>
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Sr.No</th>
                  <th className="text-left p-2">Customer Info</th>
                  <th className="text-left p-2">Ride Info</th>
                  <th className="text-left p-2">Address Info</th>
                  <th className="text-left p-2">Charges</th>
                  <th className="text-left p-2">Payment Status</th>
                  <th className="text-left p-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((booking, idx) => (
                  <tr key={booking.id} className="border-b">
                    <td className="p-2">{idx + 1}</td>
                    <td className="p-2">
                      <div>Name: {booking.customer_name}</div>
                      <div>Email: {booking.customer_email}</div>
                      <div>Mobile: {booking.customer_phone}</div>
                    </td>
                    <td className="p-2">
                      <div>Ride: {booking.trip_type === 'one_way' ? 'One Way' : 'Round Trip'}</div>
                      <div>Service: {booking.service_type}</div>
                      <div>Vehicle: {booking.vehicle_type}</div>
                      <div>No of Pacs: {booking.max_passengers || 4}</div>
                      <div>Booking No: {booking.id?.substring(0,8)}</div>
                    </td>
                    <td className="p-2">
                      <div>Pickup: {booking.pickup_location?.address}</div>
                      <div>Drop: {booking.dropoff_location?.address}</div>
                      <div>P Date: {booking.booking_date} {booking.booking_time}</div>
                    </td>
                    <td className="p-2">
                      <div>Total Miles: {booking.total_miles || 0}</div>
                      <div>Total Fare: {booking.price}</div>
                    </td>
                    <td className="p-2">{booking.payment_status === 'paid' ? 'Success' : 'Pending'}</td>
                    <td className="p-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="bg-purple-500 text-white">...</Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onClick={() => {
                            setSelectedBookingForDriver(booking);
                            setAllocateDriverDialogOpen(true);
                          }}>
                            <span className="mr-2">→</span> Allocate
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={async () => {
                            if (window.confirm('Are you sure you want to delete this booking?')) {
                              try {
                                await axios.delete(API + '/admin/bookings/' + booking.id);
                                toast.success('Booking deleted');
                                fetchBookings();
                              } catch (e) { toast.error('Failed to delete'); }
                            }
                          }}>
                            <span className="mr-2">→</span> Delete Booking
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      
      <Dialog open={allocateDriverDialogOpen} onOpenChange={setAllocateDriverDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Allocate Driver</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>Booking: {selectedBookingForDriver?.id?.substring(0,8)}</p>
            <p>Customer: {selectedBookingForDriver?.customer_name}</p>
            <Select onValueChange={async (driverId) => {
              try {
                await axios.post(API + '/admin/bookings/' + selectedBookingForDriver?.id + '/assign', { driver_id: driverId });
                toast.success('Driver assigned successfully');
                setAllocateDriverDialogOpen(false);
                fetchBookings();
              } catch (e) { toast.error('Failed to assign driver'); }
            }}>
              <SelectTrigger><SelectValue placeholder="Select Driver" /></SelectTrigger>
              <SelectContent>
                {drivers.map(d => (
                  <SelectItem key={d.id} value={d.id}>{d.name} - {d.vehicle_type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </DialogContent>
      </Dialog>

      

      <Dialog open={addBookingDialogOpen} onOpenChange={setAddBookingDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">ADD NEW BOOKING</DialogTitle>
            <div className="text-sm text-gray-500">Dispatch > New Ride</div>
          </DialogHeader>
          <div className="bg-gray-50 p-6 rounded-lg space-y-6">
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-red-500">Entry Type<span>*</span></Label>
                <Select value={newBookingData.entry_type} onValueChange={(value) => setNewBookingData({...newBookingData, entry_type: value})}>
                  <SelectTrigger className="bg-white"><SelectValue placeholder="--Select Entry Type--" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contract_entry">Contract Entry</SelectItem>
                    <SelectItem value="payment_link">Payment Link</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-red-500">UUID<span>*</span></Label>
                <Input className="bg-white" value={Math.random().toString(36).substring(2, 10)} readOnly />
              </div>
              <div className="space-y-2">
                <Label className="text-red-500">Select Customer Name<span>*</span></Label>
                <Select value={newBookingData.customer_id} onValueChange={(value) => {
                  const c = customers.find(x => x.id === value);
                  setNewBookingData({...newBookingData, customer_id: value, customer_name: c?.name || ''});
                }}>
                  <SelectTrigger className="bg-white"><SelectValue placeholder="--Select Customer--" /></SelectTrigger>
                  <SelectContent>{customers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-red-500">Select Ride Type<span>*</span></Label>
                <Select value={newBookingData.ride_type} onValueChange={(value) => setNewBookingData({...newBookingData, ride_type: value})}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_way">One Way</SelectItem>
                    <SelectItem value="round_trip">Round Trip</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-red-500">Select Service Type<span>*</span></Label>
                <Select value={newBookingData.service_type} onValueChange={(value) => setNewBookingData({...newBookingData, service_type: value})}>
                  <SelectTrigger className="bg-white"><SelectValue placeholder="--Select Service Type--" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regular_transfer">Regular Transfer</SelectItem>
                    <SelectItem value="airport_transfer">Airport Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-red-500">Select Type of Car<span>*</span></Label>
                <Select value={newBookingData.car_type} onValueChange={(value) => setNewBookingData({...newBookingData, car_type: value})}>
                  <SelectTrigger className="bg-white"><SelectValue placeholder="--Select Vehicle Type--" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="4-seater">4-Seater / Saloon</SelectItem>
                    <SelectItem value="6-seater">6-Seater</SelectItem>
                    <SelectItem value="8-seater">8-Seater</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-red-500">Max. Number of Passengers<span>*</span></Label>
                <Input className="bg-white" type="number" value={newBookingData.max_passengers} onChange={(e) => setNewBookingData({...newBookingData, max_passengers: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label className="text-red-500">Total Miles<span>*</span></Label>
                <Input className="bg-white" type="number" value={newBookingData.total_miles} onChange={(e) => setNewBookingData({...newBookingData, total_miles: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-red-500">Total Fare<span>*</span></Label>
                <Input className="bg-white" type="number" step="0.01" value={newBookingData.total_fare} onChange={(e) => setNewBookingData({...newBookingData, total_fare: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label className="text-red-500">PickUp Location<span>*</span></Label>
                <Input className="bg-white" value={newBookingData.pickup_location} onChange={(e) => setNewBookingData({...newBookingData, pickup_location: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label className="text-red-500">Drop Off Location<span>*</span></Label>
                <Input className="bg-white" value={newBookingData.drop_location} onChange={(e) => setNewBookingData({...newBookingData, drop_location: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label className="text-red-500">Pick Up Date<span>*</span></Label>
                <Input className="bg-white" type="date" value={newBookingData.pickup_date} onChange={(e) => setNewBookingData({...newBookingData, pickup_date: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-red-500">Pick Up Time<span>*</span></Label>
                <Input className="bg-white" type="time" value={newBookingData.pickup_time} onChange={(e) => setNewBookingData({...newBookingData, pickup_time: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label className="text-red-500">Drop Off Date<span>*</span></Label>
                <Input className="bg-white" type="date" value={newBookingData.dropoff_date} onChange={(e) => setNewBookingData({...newBookingData, dropoff_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label className="text-red-500">Drop Off Time<span>*</span></Label>
                <Input className="bg-white" type="time" value={newBookingData.dropoff_time} onChange={(e) => setNewBookingData({...newBookingData, dropoff_time: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label className="text-red-500">Payment Mode<span>*</span></Label>
                <Select value={newBookingData.payment_mode} onValueChange={(value) => setNewBookingData({...newBookingData, payment_mode: value})}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paypal">Paypal</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                  </SelectContent>
                </Select>
              <div class="flex gap-6 items-center py-2"><label class="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} class="w-4 h-4" /><span class="text-sm font-medium">Send Email</span></label><label class="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} class="w-4 h-4" /><span class="text-sm font-medium">Send SMS</span></label></div>
              </div>
            </div>
            <div className="flex justify-center pt-4">
              <Button className="bg-blue-500 hover:bg-blue-600 text-white px-12 py-3" onClick={async () => {
                if (!newBookingData.customer_id) { toast.error('Please select a customer'); return; }
                if (!newBookingData.pickup_location || !newBookingData.drop_location) { toast.error('Please enter locations'); return; }
                try {
                  const response = await axios.post(API + '/bookings?user_id=admin', {
                    customer_name: newBookingData.customer_name || customers.find(c => c.id === newBookingData.customer_id)?.name,
                    customer_phone: customers.find(c => c.id === newBookingData.customer_id)?.phone || '',
                    customer_email: customers.find(c => c.id === newBookingData.customer_id)?.email || '',
                    pickup_location: { address: newBookingData.pickup_location, lat: 51.5074, lng: -0.1278 },
                    dropoff_location: { address: newBookingData.drop_location, lat: 51.5074, lng: -0.1278 },
                    vehicle_type: newBookingData.car_type || 'saloon',
                    trip_type: newBookingData.ride_type,
                    booking_date: newBookingData.pickup_date,
                    booking_time: newBookingData.pickup_time,
                    price: parseFloat(newBookingData.total_fare) || 0,
                    service_type: newBookingData.service_type,
                    payment_mode: newBookingData.payment_mode,
                    max_passengers: parseInt(newBookingData.max_passengers) || 4,
                    total_miles: parseFloat(newBookingData.total_miles) || 0
                  });
                  if (response.data.success) {
                    toast.success('Booking created!');
                    setAddBookingDialogOpen(false);
                    fetchBookings();
                  }
                } catch (error) { toast.error('Failed: ' + (error.response?.data?.detail || error.message)); }
              }}>Submit</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Contract Dialog */}
      <Dialog open={addContractDialogOpen} onOpenChange={setAddContractDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Contract</DialogTitle>
            <DialogDescription>Create a new ride contract</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Row 1: Contract Type, Title, Number */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contract-type">Contract Type *</Label>
                <Select value={newContract.contract_type} onValueChange={(value) => setNewContract({...newContract, contract_type: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DSA">DSA</SelectItem>
                    <SelectItem value="Organisation">Organisation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="contract-title">Contract Title *</Label>
                <Input
                  id="contract-title"
                  placeholder="Enter contract title"
                  value={newContract.contract_title}
                  onChange={(e) => setNewContract({...newContract, contract_title: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contract-number">Contract Number *</Label>
                <Input
                  id="contract-number"
                  placeholder="Enter contract number"
                  value={newContract.contract_number}
                  onChange={(e) => setNewContract({...newContract, contract_number: e.target.value})}
                />
              </div>
            </div>

            {/* Row 2: Select Customer */}
            <div className="space-y-2">
              <Label htmlFor="customer">Select Customer *</Label>
              <Select value={newContract.customer_id} onValueChange={(value) => setNewContract({...newContract, customer_id: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(customer => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name} - {customer.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Row 3: Ride Type, Service Type */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ride-type">Ride Type *</Label>
                <Select value={newContract.ride_type} onValueChange={(value) => setNewContract({...newContract, ride_type: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="One Way">One Way</SelectItem>
                    <SelectItem value="Round Trip">Round Trip</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="service-type">Service Type *</Label>
                <Select value={newContract.service_type} onValueChange={(value) => setNewContract({...newContract, service_type: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Airport Transport">Airport Transport</SelectItem>
                    <SelectItem value="Regular Transport">Regular Transport</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 4: Car Type, Max Passengers */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="car-type">Car Type *</Label>
                <Select value={newContract.car_type} onValueChange={(value) => setNewContract({...newContract, car_type: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="saloon">Saloon</SelectItem>
                    <SelectItem value="estate">Estate</SelectItem>
                    <SelectItem value="mvp">MVP</SelectItem>
                    <SelectItem value="luxury">Luxury</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="max-passengers">Max Passengers *</Label>
                <Input
                  id="max-passengers"
                  type="number"
                  min="1"
                  max="8"
                  value={newContract.max_passengers}
                  onChange={(e) => setNewContract({...newContract, max_passengers: parseInt(e.target.value) || 4})}
                />
              </div>
            </div>

            {/* Row 5: Total Miles, Total Fare */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="total-miles">Total Miles *</Label>
                <Input
                  id="total-miles"
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="0"
                  value={newContract.total_miles}
                  onChange={(e) => setNewContract({...newContract, total_miles: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="total-fare">Total Fare (£) *</Label>
                <Input
                  id="total-fare"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={newContract.total_fare}
                  onChange={(e) => setNewContract({...newContract, total_fare: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            {/* Row 6: Pickup Location, Drop Location */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pickup-location">Pickup Location *</Label>
                <Input
                  id="pickup-location"
                  placeholder="Enter pickup location"
                  value={newContract.pickup_location}
                  onChange={(e) => setNewContract({...newContract, pickup_location: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="drop-location">Drop Location *</Label>
                <Input
                  id="drop-location"
                  placeholder="Enter drop location"
                  value={newContract.drop_location}
                  onChange={(e) => setNewContract({...newContract, drop_location: e.target.value})}
                />
              </div>
            </div>

            {/* Row 7: Pickup Time, Drop Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pickup-time">Pickup Time *</Label>
                <Input
                  id="pickup-time"
                  type="datetime-local"
                  value={newContract.pickup_time}
                  onChange={(e) => setNewContract({...newContract, pickup_time: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="drop-time">Drop Time *</Label>
                <Input
                  id="drop-time"
                  type="datetime-local"
                  value={newContract.drop_time}
                  onChange={(e) => setNewContract({...newContract, drop_time: e.target.value})}
                />
              </div>
            </div>

            {/* Return Trip Fields - Show only if Round Trip is selected */}
            {newContract.ride_type === "Round Trip" && (
              <>
                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold mb-4">Return Trip Details</h3>
                  
                  {/* Return Pickup Location, Return Drop Location */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="space-y-2">
                      <Label htmlFor="return-pickup-location">Return Pickup Location *</Label>
                      <Input
                        id="return-pickup-location"
                        placeholder="Enter return pickup location"
                        value={newContract.return_pickup_location}
                        onChange={(e) => setNewContract({...newContract, return_pickup_location: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="return-drop-location">Return Drop Location *</Label>
                      <Input
                        id="return-drop-location"
                        placeholder="Enter return drop location"
                        value={newContract.return_drop_location}
                        onChange={(e) => setNewContract({...newContract, return_drop_location: e.target.value})}
                      />
                    </div>
                  </div>

                  {/* Return Pickup Time, Return Drop Time */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="return-pickup-time">Return Pickup Time *</Label>
                      <Input
                        id="return-pickup-time"
                        type="datetime-local"
                        value={newContract.return_pickup_time}
                        onChange={(e) => setNewContract({...newContract, return_pickup_time: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="return-drop-time">Return Drop Time *</Label>
                      <Input
                        id="return-drop-time"
                        type="datetime-local"
                        value={newContract.return_drop_time}
                        onChange={(e) => setNewContract({...newContract, return_drop_time: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Payment Details */}
            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold mb-4">Payment Details</h3>
              <div className="space-y-2">
                <Label>Driver Payment Type *</Label>
                <Select value={newContract.payment_type} onValueChange={(v) => setNewContract({...newContract, payment_type: v})}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flat">Flat Amount</SelectItem>
                    <SelectItem value="percentage">Percentage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="driver-payable">{newContract.payment_type === "percentage" ? "Driver Payable (%)" : "Driver Payable (£)"} *</Label>
                  <Input
                    id="driver-payable"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={newContract.driver_payable}
                    onChange={(e) => setNewContract({...newContract, driver_payable: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="roman-cars-payment">Roman Cars Payment (£) *</Label>
                  <Input
                    id="roman-cars-payment"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={newContract.roman_cars_payment}
                    onChange={(e) => setNewContract({...newContract, roman_cars_payment: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>
            </div>


            {newContract.contract_type === "DSA" && (
              <div className="space-y-2 mb-4">
                <Label htmlFor="dsa-amount">DSA Amount *</Label>
                <Input
                  id="dsa-amount"
                  type="number"
                  placeholder="Enter DSA amount"
                  value={newContract.dsa_amount || ""}
                  onChange={(e) => setNewContract({...newContract, dsa_amount: e.target.value})}
                />
              </div>
            )}
            <div className="flex gap-6 items-center py-3 px-2 bg-gray-50 rounded-lg">
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="w-4 h-4" /><span className="text-sm font-medium">Send Email</span></label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} className="w-4 h-4" /><span className="text-sm font-medium">Send SMS</span></label>
            </div>
            <Button onClick={handleAddContract} className="w-full" size="lg">
              <Plus size={16} className="mr-2" />
              Create Contract
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Manage Contracts Dialog */}
      <Dialog open={manageContractsDialogOpen} onOpenChange={setManageContractsDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex justify-between items-center">
              <DialogTitle className="text-2xl font-bold">View Contracts</DialogTitle>
              <Button 
                className="bg-teal-600 hover:bg-teal-700 text-white"
                onClick={() => {
                  setManageContractsDialogOpen(false);
                  setAddContractDialogOpen(true);
                }}
              >
                <Plus size={16} className="mr-2" />
                Add Contract
              </Button>
            </div>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex flex-wrap justify-between items-center gap-4">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => {
                  const tableData = contracts.map((c, i) => `${i+1}\t${c.contract_title || ''}\t${c.contract_number || ''}\t${customers.find(cust => cust.id === c.customer_id)?.name || ''}`).join('\n');
                  navigator.clipboard.writeText(`Sr No\tContract Title\tContract No\tCustomer Name\n${tableData}`);
                  toast.success('Table copied to clipboard!');
                }}>Copy</Button>
                <Button variant="outline" size="sm" onClick={() => {
                  const headers = 'Sr No,Contract Title,Contract No,Customer Name,Status';
                  const rows = contracts.map((c, i) => {
                    const customerName = customers.find(cust => cust.id === c.customer_id)?.name || '';
                    return `${i+1},"${c.contract_title || ''}","${c.contract_number || ''}","${customerName}","${c.status || 'active'}"`;
                  }).join('\n');
                  const csv = `${headers}\n${rows}`;
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'contracts.csv';
                  a.click();
                  toast.success('CSV downloaded!');
                }}>CSV</Button>
                <Button variant="outline" size="sm" onClick={() => {
                  const headers = 'Sr No\tContract Title\tContract No\tCustomer Name\tStatus';
                  const rows = contracts.map((c, i) => {
                    const customerName = customers.find(cust => cust.id === c.customer_id)?.name || '';
                    return `${i+1}\t${c.contract_title || ''}\t${c.contract_number || ''}\t${customerName}\t${c.status || 'active'}`;
                  }).join('\n');
                  const blob = new Blob([`${headers}\n${rows}`], { type: 'application/vnd.ms-excel' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'contracts.xls';
                  a.click();
                  toast.success('Excel downloaded!');
                }}>Excel</Button>
                <Button variant="outline" size="sm" onClick={() => printInvoice()}>Print</Button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Search:</span>
                <Input placeholder="Search contracts..." value={contractSearchQuery} onChange={(e) => setContractSearchQuery(e.target.value)} className="w-64" />
              </div>
            </div>

            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Sr No</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Contract Title</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Contract No</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Customer Name</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {contracts.length === 0 ? (
                    <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-500">No contracts found. Click "Add Contract" to create one.</td></tr>
                  ) : (
                    contracts.filter(contract => {
                      if (!contractSearchQuery) return true;
                      const searchLower = contractSearchQuery.toLowerCase();
                      const customerName = customers.find(c => c.id === contract.customer_id)?.name || '';
                      return (contract.contract_title || '').toLowerCase().includes(searchLower) || (contract.contract_number || '').toLowerCase().includes(searchLower) || customerName.toLowerCase().includes(searchLower);
                    }).map((contract, index) => {
                      const customer = customers.find(c => c.id === contract.customer_id);
                      const isInactive = contract.status === 'inactive';
                      return (
                        <tr key={contract.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">{index + 1}</td>
                          <td className="px-4 py-3"><div className="flex items-center gap-2">{isInactive && <span className="w-2 h-2 bg-pink-500 rounded-full"></span>}<span>{contract.contract_title || 'N/A'}</span>{isInactive && <span className="text-pink-500 text-xs">(Inactive)</span>}</div></td>
                          <td className="px-4 py-3">{contract.contract_number || 'N/A'}</td>
                          <td className="px-4 py-3">{customer?.name || 'N/A'}</td>
                          <td className="px-4 py-3">
                            <Select onValueChange={(action) => {
                if (action === 'view') { setViewingContract(contract); setViewContractDialogOpen(true); }
                else if (action === 'edit') { setEditingContract({...contract}); setEditContractDialogOpen(true); }
                      else if (action === 'delete') { if (window.confirm('Delete contract '+contract.contract_title+'?')) { axios.delete(API + '/admin/contracts/' + contract.id).then(() => { setContracts(contracts.filter(c => c.id !== contract.id)); toast.success('Contract deleted'); }).catch(() => toast.error('Failed to delete')); } }
                              else if (action === 'toggle-status') { setContracts(contracts.map(c => c.id === contract.id ? {...c, status: c.status === 'active' ? 'inactive' : 'active'} : c)); toast.success(`Contract ${contract.status === 'active' ? 'deactivated' : 'activated'}`); }
                            }}>
                              <SelectTrigger className="w-20 bg-indigo-100 border-indigo-200"><SelectValue placeholder="..." /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="view">View</SelectItem>
                                <SelectItem value="edit">Edit</SelectItem>
                                <SelectItem value="toggle-status">{contract.status === 'active' ? 'Deactivate' : 'Activate'}</SelectItem>
                                <SelectItem value="delete">Delete</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between items-center text-sm text-gray-600">
              <span>Showing {contracts.filter(c => { if (!contractSearchQuery) return true; const s = contractSearchQuery.toLowerCase(); const cn = customers.find(x => x.id === c.customer_id)?.name || ''; return (c.contract_title || '').toLowerCase().includes(s) || (c.contract_number || '').toLowerCase().includes(s) || cn.toLowerCase().includes(s); }).length} of {contracts.length} entries</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>



      {/* Contract Report Dialog */}
      <Dialog open={contractReportDialogOpen} onOpenChange={setContractReportDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Generate Contract Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="font-semibold">Select Contract<span className="text-red-500">*</span></Label>
                <Select value={contractReportData.contract_id} onValueChange={(value) => setContractReportData({...contractReportData, contract_id: value})}>
                  <SelectTrigger className="h-12"><SelectValue placeholder="--Select Contract--" /></SelectTrigger>
                  <SelectContent>
                    {contracts.length === 0 ? (<SelectItem value="none" disabled>No contracts available</SelectItem>) : (contracts.map(contract => (<SelectItem key={contract.id} value={contract.id}>{contract.contract_title} ({contract.contract_number})</SelectItem>)))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-semibold">From Date<span className="text-red-500">*</span></Label>
                <Input type="date" className="h-12" value={contractReportData.from_date} onChange={(e) => setContractReportData({...contractReportData, from_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label className="font-semibold">To Date<span className="text-red-500">*</span></Label>
                <Input type="date" className="h-12" value={contractReportData.to_date} onChange={(e) => setContractReportData({...contractReportData, to_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label className="font-semibold">Invoice NOTE</Label>
                <textarea className="w-full h-12 px-3 py-2 border rounded-md resize-none" placeholder="Enter notes..." value={contractReportData.invoice_note} onChange={(e) => setContractReportData({...contractReportData, invoice_note: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4 items-end">
              <div className="space-y-2">
                <Label className="font-semibold">Invoice No</Label>
                <Input className="h-12" placeholder="Enter invoice number" value={contractReportData.invoice_no} onChange={(e) => setContractReportData({...contractReportData, invoice_no: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label className="font-semibold">Invoice Date<span className="text-red-500">*</span></Label>
                <Input type="date" className="h-12" value={contractReportData.invoice_date} onChange={(e) => setContractReportData({...contractReportData, invoice_date: e.target.value})} />
              </div>
              <div></div>
              <div>
                <Button className="w-full h-12 bg-blue-500 hover:bg-blue-600 text-white text-lg" onClick={async () => { if (!contractReportData.contract_id || !contractReportData.from_date || !contractReportData.to_date || !contractReportData.invoice_date) { toast.error('Please fill all required fields'); return; } const contract = contracts.find(c => c.id === contractReportData.contract_id); const customer = customers.find(c => c.id === contract?.customer_id); let filteredRides = []; try { const res = await axios.get(API + "/admin/driver-report?from_date=" + contractReportData.from_date + "&to_date=" + contractReportData.to_date); filteredRides = (res.data.bookings || []).filter(b => b.contract_id === contractReportData.contract_id); } catch(e) { filteredRides = bookings.filter(b => { const d = new Date(b.pickup_date || b.booking_date || b.created_at); return b.contract_id === contractReportData.contract_id && d >= new Date(contractReportData.from_date) && d <= new Date(contractReportData.to_date); }); } setContractInvoiceData({ contract, customer, rides: filteredRides.sort((a, b) => new Date(a.pickup_date || a.booking_date) - new Date(b.pickup_date || b.booking_date)), invoice_no: contractReportData.invoice_no || Math.floor(Math.random() * 1000), invoice_date: contractReportData.invoice_date, invoice_note: contractReportData.invoice_note }); setContractReportDialogOpen(false); setContractInvoiceDialogOpen(true); if (filteredRides.length > 0) setSelectedInvoice(filteredRides[0]); }}>View Invoice</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Customers Dialog */}
      <Dialog open={manageCustomersDialogOpen} onOpenChange={setManageCustomersDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex justify-between items-center">
              <DialogTitle className="text-2xl font-bold">View Customers</DialogTitle>
              <Button className="bg-gray-800 hover:bg-gray-900 text-white" onClick={() => { setManageCustomersDialogOpen(false); setAddCustomerDialogOpen(true); }}>
                <Plus size={16} className="mr-2" />Add Customer
              </Button>
            </div>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap justify-between items-center gap-4">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { const tableData = customers.map((c, i) => `${i+1}\t${c.name || ''}\t${c.email || ''}\t${c.phone || ''}\t${c.address || ''}`).join('\n'); navigator.clipboard.writeText(`Sr No\tName\tEmail\tPhone Number\tAddress\n${tableData}`); toast.success('Table copied!'); }}>Copy</Button>
                <Button variant="outline" size="sm" onClick={() => { const headers = 'Sr No,Name,Email,Phone Number,Address'; const rows = customers.map((c, i) => `${i+1},"${c.name || ''}","${c.email || ''}","${c.phone || ''}","${c.address || ''}"`).join('\n'); const blob = new Blob([`${headers}\n${rows}`], { type: 'text/csv' }); const url = window.URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'customers.csv'; a.click(); toast.success('CSV downloaded!'); }}>CSV</Button>
                <Button variant="outline" size="sm" onClick={() => { const headers = 'Sr No\tName\tEmail\tPhone Number\tAddress'; const rows = customers.map((c, i) => `${i+1}\t${c.name || ''}\t${c.email || ''}\t${c.phone || ''}\t${c.address || ''}`).join('\n'); const blob = new Blob([`${headers}\n${rows}`], { type: 'application/vnd.ms-excel' }); const url = window.URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'customers.xls'; a.click(); toast.success('Excel downloaded!'); }}>Excel</Button>
                <Button variant="outline" size="sm" onClick={() => printInvoice()}>Print</Button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Search:</span>
                <Input placeholder="Search customers..." value={customerSearchQuery} onChange={(e) => setCustomerSearchQuery(e.target.value)} className="w-64" />
              </div>
            </div>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Sr No</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Name</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Email</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Phone Number</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Address</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {customers.length === 0 ? (<tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500">No customers found. Click "Add Customer" to create one.</td></tr>) : (
                    customers.filter(customer => { if (!customerSearchQuery) return true; const s = customerSearchQuery.toLowerCase(); return (customer.name || '').toLowerCase().includes(s) || (customer.email || '').toLowerCase().includes(s) || (customer.phone || '').toLowerCase().includes(s) || (customer.address || '').toLowerCase().includes(s); }).map((customer, index) => (
                      <tr key={customer.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">{index + 1}</td>
                        <td className="px-4 py-3 font-medium">{customer.name || 'N/A'}</td>
                        <td className="px-4 py-3">{customer.email || 'N/A'}</td>
                        <td className="px-4 py-3">{customer.phone || 'N/A'}</td>
                        <td className="px-4 py-3">{customer.address || 'N/A'}</td>
                        <td className="px-4 py-3">
                          <Select onValueChange={(action) => { if (action === 'view') toast.info(`Viewing: ${customer.name}`); else if (action === 'edit') { setEditingCustomer(customer); setEditCustomerDialogOpen(true); setManageCustomersDialogOpen(false); } else if (action === 'delete') { if (window.confirm(`Delete "${customer.name}"?`)) axios.delete(`${API}/admin/customers/${customer.id}`).then(() => { toast.success('Deleted!'); fetchCustomers(); }).catch(() => toast.error('Failed')); } }}>
                            <SelectTrigger className="w-20 bg-indigo-100 border-indigo-200"><SelectValue placeholder="..." /></SelectTrigger>
                            <SelectContent><SelectItem value="view">View</SelectItem><SelectItem value="edit">Edit</SelectItem><SelectItem value="delete">Delete</SelectItem></SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="text-sm text-gray-600"><span>Showing {customers.filter(c => { if (!customerSearchQuery) return true; const s = customerSearchQuery.toLowerCase(); return (c.name || '').toLowerCase().includes(s) || (c.email || '').toLowerCase().includes(s) || (c.phone || '').toLowerCase().includes(s) || (c.address || '').toLowerCase().includes(s); }).length} of {customers.length} entries</span></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice View Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-white p-0">
          {selectedInvoice && (
            <div className="invoice-container" id="invoice-print">
              <div className="flex justify-between items-center p-4 border-b">
                <div className="flex items-center gap-3">
                  <img src="/brand-logo.png" alt="Roman Cars Logo" className="h-16" onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
                <div className="text-right"><h2 className="text-2xl font-bold">Booking Invoice</h2></div>
              </div>
              <div className="p-4">
                <div className="bg-yellow-300 px-4 py-2 mb-4 flex justify-between">
                  <p><strong>Customer Name:</strong> {selectedInvoice?.customer_name || 'N/A'}</p>
                  <p><strong>Customer reference number:</strong> {selectedInvoice?.invoiceNumber || 'N/A'}</p>
                </div>
                <div className="grid grid-cols-2 gap-8 mb-4 text-sm">
                  <div className="space-y-1"><p><strong>Supplier Name:</strong> {siteSettings?.logo_text || 'Roman Cars'}{siteSettings?.contact?.address ? ` - ${siteSettings.contact.address}` : ''}</p><p><strong>License number:</strong> LN202300</p></div>
                  <div className="space-y-1 text-right"><p><strong>End of contract:</strong> {selectedInvoice?.customer_name || 'N/A'}</p><p><strong>Invoice No:</strong> {selectedInvoice?.invoiceNumber || 'N/A'}</p></div>
                </div>
                <div className="bg-yellow-300 px-4 py-2 mb-4"><p className="text-sm"><strong>Invoice date:</strong> {(selectedInvoice || {}).booking_date || new Date().toLocaleDateString('en-GB')}</p></div>
                <div className="grid grid-cols-2 gap-8 mb-4 text-sm">
                  <div className="space-y-1"><p><strong>Metro Bank Sort code:</strong> 230580</p><p><strong>AC number:</strong> 31961475</p></div>
                  <div className="space-y-1 text-right"><p><strong>Email:</strong> {(selectedInvoice || {}).customer_email || 'N/A'}</p><p><strong>Address:</strong> {(selectedInvoice || {}).pickup_location?.address || 'N/A'}</p></div>
                </div>
                <div className="bg-yellow-300 px-4 py-2 mb-2"><h3 className="font-bold text-lg">Details</h3></div>
                <div className="mb-4"><div className="overflow-x-auto"><table className="w-full text-sm border border-gray-400"><thead className="bg-gray-200"><tr><th className="border border-gray-400 px-3 py-2 text-left font-bold">Date</th><th className="border border-gray-400 px-3 py-2 text-left font-bold">Time</th><th className="border border-gray-400 px-3 py-2 text-left font-bold">Pickup</th><th className="border border-gray-400 px-3 py-2 text-left font-bold">Drop off</th><th className="border border-gray-400 px-3 py-2 text-right font-bold">Total Fare</th><th className="border border-gray-400 px-3 py-2 text-right font-bold">Subtotal</th></tr></thead><tbody><tr className="hover:bg-gray-50"><td className="border border-gray-400 px-3 py-2">{(selectedInvoice || {}).booking_date || 'N/A'}</td><td className="border border-gray-400 px-3 py-2">{(selectedInvoice || {}).booking_time || '00:00:00'} To {(selectedInvoice || {}).dropoff_time || '00:00:00'}</td><td className="border border-gray-400 px-3 py-2">{(selectedInvoice || {}).pickup_location?.address || 'N/A'}</td><td className="border border-gray-400 px-3 py-2">{(selectedInvoice || {}).dropoff_location?.address || 'N/A'}</td><td className="border border-gray-400 px-3 py-2 text-right">£ {((selectedInvoice || {}).price || 0).toFixed(2)}</td><td className="border border-gray-400 px-3 py-2 text-right">£ {((selectedInvoice || {}).price || 0).toFixed(2)}</td></tr></tbody></table></div></div>
                <div className="bg-yellow-300 px-4 py-2 mb-4 text-right"><p className="text-xl font-bold">Total Payable: £ {((selectedInvoice || {}).price || 0).toFixed(2)}</p></div>
                <div className="mb-4 px-4"><p className="text-sm"><strong>NOTE:</strong> Driver: {(selectedInvoice || {}).driver?.name || 'N/A'} | Vehicle: {(selectedInvoice || {}).vehicle_type || 'N/A'}</p></div>
                <div className="flex gap-4 justify-center mt-6 no-print">
                  <Button variant="outline" onClick={() => setInvoiceDialogOpen(false)}>Close</Button>
                  <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => { const csv = "Date,Time,Pickup,Drop off,Total Fare,Subtotal\n" + selectedInvoice?.booking_date + "," + selectedInvoice?.booking_time + "," + (selectedInvoice?.pickup_location?.address || '') + "," + (selectedInvoice?.dropoff_location?.address || '') + "," + (selectedInvoice?.price || 0).toFixed(2) + "," + (selectedInvoice?.price || 0).toFixed(2); const blob = new Blob([csv], {type: 'application/vnd.ms-excel'}); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'invoice_' + selectedInvoice?.invoiceNumber + '.xls'; a.click(); toast.success('Excel downloaded!'); }}>Download Excel</Button>
                  <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => printInvoice()}>Download PDF</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>



      {/* Driver Report Dialog */}
      <Dialog open={driverReportDialogOpen} onOpenChange={setDriverReportDialogOpen}>
        <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Driver Report</DialogTitle>
          </DialogHeader>
          {!driverReportResults ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>From Date (DD/MM/YYYY)</Label>
                  <Input type="date" value={driverReportData.from_date} onChange={(e) => setDriverReportData({...driverReportData, from_date: e.target.value})}/>
                </div>
                <div className="space-y-2">
                  <Label>To Date (DD/MM/YYYY)</Label>
                  <Input type="date" value={driverReportData.to_date} onChange={(e) => setDriverReportData({...driverReportData, to_date: e.target.value})}/>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-semibold">Driver Category</Label>
                <Select value={driverReportData.driver_category} onValueChange={(v) => setDriverReportData({...driverReportData, driver_category: v === "all" ? "" : v})}>
                  <SelectTrigger><SelectValue placeholder="All Categories" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {driverCategories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Select Driver</Label>
                <Select value={driverReportData.driver_id} onValueChange={(value) => setDriverReportData({...driverReportData, driver_id: value, select_all_drivers: false})} disabled={driverReportData.select_all_drivers}>
                  <SelectTrigger><SelectValue placeholder="Select driver" /></SelectTrigger>
                  <SelectContent>{drivers.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="selectAllDrivers" checked={driverReportData.select_all_drivers} onChange={(e) => setDriverReportData({...driverReportData, select_all_drivers: e.target.checked, driver_id: ""})} className="w-4 h-4"/>
                <Label htmlFor="selectAllDrivers">Select All Drivers</Label>
              </div>
              <div className="space-y-2">
                <Label>Supplier (Optional)</Label>
                <Input placeholder="Enter supplier name" value={driverReportData.supplier} onChange={(e) => setDriverReportData({...driverReportData, supplier: e.target.value})}/>
              </div>
              <Button className="w-full" variant="outline" onClick={async () => { const params = new URLSearchParams(); if (driverReportData.from_date) params.append("from_date", driverReportData.from_date); if (driverReportData.to_date) params.append("to_date", driverReportData.to_date); if (!driverReportData.select_all_drivers && driverReportData.driver_id) params.append("driver_id", driverReportData.driver_id); if (driverReportData.driver_category) params.append("driver_category", driverReportData.driver_category); const res = await axios.get(API + "/admin/driver-report?" + params.toString()); setDriverReportResults(res.data.bookings || []); toast.success("Found " + (res.data.total || 0) + " bookings"); }}>View Data</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="max-h-60 overflow-y-auto border rounded">
                  <table className="w-full text-xs border-collapse">
                    <thead className="bg-gray-100 sticky top-0"><tr><th className="p-2 text-left border">Booking Ref</th><th className="p-2 text-left border">Date</th><th className="p-2 text-left border">Time</th><th className="p-2 text-left border">Pick Up</th><th className="p-2 text-left border">Drop Off</th><th className="p-2 text-left border">Name</th><th className="p-2 text-left border">Contact No.</th><th className="p-2 text-left border">Flight Number</th><th className="p-2 text-left border">Driver Name</th><th className="p-2 text-left border">Driver Number</th><th className="p-2 text-left border">Status</th><th className="p-2 text-right border">Driver Fare</th></tr></thead>
                    <tbody>{driverReportResults.map((b, i) => { const drv = drivers.find(d => d.id === b.driver_id); return (<tr key={i} className="border-b hover:bg-gray-50"><td className="p-2 border">{b.booking_ref || (b.id ? b.id.slice(0,8) : "N/A")}</td><td className="p-2 border">{new Date(b.booking_date || b.pickup_date || b.created_at).toLocaleDateString("en-GB")}</td><td className="p-2 border">{b.booking_time || b.pickup_time || "N/A"}</td><td className="p-2 border min-w-[150px] max-w-[150px]">{b.pickup_location && b.pickup_location.address ? b.pickup_location.address : (b.pickup_address || "N/A")}</td><td className="p-2 border min-w-[150px] max-w-[150px]">{b.dropoff_location && b.dropoff_location.address ? b.dropoff_location.address : (b.dropoff_address || "N/A")}</td><td className="p-2 border">{b.customer_name || "N/A"}</td><td className="p-2 border">{b.customer_phone || "N/A"}</td><td className="p-2 border">{b.flight_number || "N/A"}</td><td className="p-2 border">{drv ? drv.name : "Unassigned"}</td><td className="p-2 border">{drv ? drv.phone : "N/A"}</td><td className="p-2 border">{b.status || "pending"}</td><td className="p-2 border text-right">{(b.paid_amount || 0).toFixed(2)}</td></tr>); })}</tbody>
                  </table>
              </div>
              <div className="text-right font-bold">Total: £{driverReportResults.reduce((sum, b) => sum + (b.paid_amount || 0), 0).toFixed(2)} ({driverReportResults.length} rides)</div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setDriverReportResults(null)}>Back</Button>
                <Button className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={() => { const csv = "Date,Driver,Customer,Fare\n" + driverReportResults.map(b => b.booking_date + "," + (drivers.find(d => d.id === b.driver_id)?.name || "N/A") + "," + b.customer_name + "," + b.paid_amount || 0).join("\n"); const blob = new Blob([csv], {type: "text/csv"}); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "driver_report.csv"; a.click(); toast.success("Report downloaded!"); }}>Download Excel</Button>
                <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={() => printInvoice()}>Download PDF</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      

      {/* Ride Report Dialog */}
      <Dialog open={rideReportDialogOpen} onOpenChange={setRideReportDialogOpen}>
        <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Ride Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-4 items-end">
              <div className="space-y-2">
                <Label className="text-red-500">Start Date*</Label>
                <Input type="date" className="w-48" value={rideReportData?.startDate || ''} onChange={(e) => setRideReportData({...rideReportData, startDate: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label className="text-red-500">End Date*</Label>
                <Input type="date" className="w-48" value={rideReportData?.endDate || ''} onChange={(e) => setRideReportData({...rideReportData, endDate: e.target.value})} />
              </div>
          <Button className="bg-blue-500 hover:bg-blue-600 text-white" onClick={async () => {
            const res = await axios.get(API + "/admin/driver-report?from_date=" + (rideReportData?.startDate || "") + "&to_date=" + (rideReportData?.endDate || ""));
            setRideReportResults(res.data.bookings || []);
            toast.success("Found " + (res.data.total || 0) + " bookings");
          }}>Search</Button>
            </div>
            <div className="flex gap-2 mb-4">
              <Button variant="outline" size="sm">Copy</Button>
              <Button variant="outline" size="sm">CSV</Button>
              <Button variant="outline" size="sm">Excel</Button>
              <Button variant="outline" size="sm">Print</Button>
              <div className="ml-auto"><Input placeholder="Search:" className="w-48" /></div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100 border-b">
                    <th className="text-left p-2 border">ID</th>
                    <th className="text-left p-2 border">Date</th>
                    <th className="text-left p-2 border">Customer Details</th>
                    <th className="text-left p-2 border">Ride Details</th>
                    <th className="text-left p-2 border">Driver Details</th>
                    <th className="text-left p-2 border">Vehicle Details</th>
                    <th className="text-left p-2 border">PickUp Location</th>
                    <th className="text-left p-2 border">DropOff Location</th>
                  </tr>
                </thead>
                <tbody>
                  {(rideReportResults || bookings).map((booking, idx) => {
                    const driver = drivers.find(d => d.id === booking.driver_id);
                    return (
                      <tr key={booking.id} className="border-b">
                        <td className="p-2 border">{idx + 1}</td>
                        <td className="p-2 border">{booking.booking_date}<br/>{booking.booking_time}</td>
                        <td className="p-2 border">
                          <div><b>Name:</b> {booking.customer_name}</div>
                          <div><b>Email Id:</b> {booking.customer_email}</div>
                          <div><b>Phone No.:</b> {booking.customer_phone}</div>
                        </td>
                        <td className="p-2 border">
                          <div><b>Ride Type:</b> {booking.trip_type === 'one_way' ? 'One Way' : 'Round Trip'}</div>
                          <div><b>Service Type:</b> {booking.service_type || 'Regular Transfer'}</div>
                          <div><b>No. of Passengers:</b> {booking.max_passengers || 1}</div>
                          <div><b>Total Miles:</b> {booking.total_miles || 0}</div>
                          <div><b>Total Fare:</b> {booking.price || 0}</div>
                        </td>
                        <td className="p-2 border">
                          <div><b>Driver Name:</b> {driver?.name || 'Not Assigned'}</div>
                          <div><b>NI Number:</b> {driver?.ni_number || '-'}</div>
                          <div><b>Email Id:</b> {driver?.email || '-'}</div>
                          <div><b>Phone No.:</b> {driver?.phone || '-'}</div>
                        </td>
                        <td className="p-2 border">
                          <div><b>Vehicle Number:</b> {driver?.license_plate || driver?.car_registration || driver?.vehicle_number || '-'}</div>
                      <div><b>Car:</b> {driver?.car_make} {driver?.car_model} ({driver?.car_colour || '-'})</div>
                          <div><b>Vehicle Licence:</b> {driver?.vehicle_licence || '-'}</div>
                          <div><b>Vehicle Licence Expiry:</b> {driver?.licence_expiry || '-'}</div>
                          <div><b>Vehicle Type:</b> {driver?.vehicle_type || '-'}</div>
                          <div><b>Vehicle Model:</b> {driver?.vehicle_model || '-'}</div>
                        </td>
                        <td className="p-2 border">{booking.pickup_location?.address || '-'}</td>
                        <td className="p-2 border">{booking.dropoff_location?.address || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contract Report Dialog */}
      <Dialog open={contractReportDialogOpen} onOpenChange={setContractReportDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Generate Contract Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="font-semibold">Select Contract<span className="text-red-500">*</span></Label>
                <Select value={contractReportData.contract_id} onValueChange={(value) => setContractReportData({...contractReportData, contract_id: value})}>
                  <SelectTrigger className="h-12"><SelectValue placeholder="--Select Contract--" /></SelectTrigger>
                  <SelectContent>
                    {contracts.length === 0 ? (<SelectItem value="none" disabled>No contracts available</SelectItem>) : (contracts.map(contract => (<SelectItem key={contract.id} value={contract.id}>{contract.contract_title} ({contract.contract_number})</SelectItem>)))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-semibold">From Date<span className="text-red-500">*</span></Label>
                <Input type="date" className="h-12" value={contractReportData.from_date} onChange={(e) => setContractReportData({...contractReportData, from_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label className="font-semibold">To Date<span className="text-red-500">*</span></Label>
                <Input type="date" className="h-12" value={contractReportData.to_date} onChange={(e) => setContractReportData({...contractReportData, to_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label className="font-semibold">Invoice NOTE</Label>
                <textarea className="w-full h-12 px-3 py-2 border rounded-md resize-none" placeholder="Enter notes..." value={contractReportData.invoice_note} onChange={(e) => setContractReportData({...contractReportData, invoice_note: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4 items-end">
              <div className="space-y-2">
                <Label className="font-semibold">Invoice No</Label>
                <Input className="h-12" placeholder="Enter invoice number" value={contractReportData.invoice_no} onChange={(e) => setContractReportData({...contractReportData, invoice_no: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label className="font-semibold">Invoice Date<span className="text-red-500">*</span></Label>
                <Input type="date" className="h-12" value={contractReportData.invoice_date} onChange={(e) => setContractReportData({...contractReportData, invoice_date: e.target.value})} />
              </div>
              <div></div>
              <div>
                <Button className="w-full h-12 bg-blue-500 hover:bg-blue-600 text-white text-lg" onClick={async () => { if (!contractReportData.contract_id || !contractReportData.from_date || !contractReportData.to_date || !contractReportData.invoice_date) { toast.error('Please fill all required fields'); return; } const contract = contracts.find(c => c.id === contractReportData.contract_id); const customer = customers.find(c => c.id === contract?.customer_id); const filteredRides = bookings.filter(b => { const d = new Date(b.pickup_date || b.booking_date || b.created_at); return b.contract_id === contractReportData.contract_id && d >= new Date(contractReportData.from_date) && d <= new Date(contractReportData.to_date); }); setContractInvoiceData({ contract, customer, rides: filteredRides.sort((a, b) => new Date(a.pickup_date || a.booking_date) - new Date(b.pickup_date || b.booking_date)), invoice_no: contractReportData.invoice_no || Math.floor(Math.random() * 1000), invoice_date: contractReportData.invoice_date, invoice_note: contractReportData.invoice_note }); setContractReportDialogOpen(false); setContractInvoiceDialogOpen(true); if (filteredRides.length > 0) setSelectedInvoice(filteredRides[0]); }}>View Invoice</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Customers Dialog */}
      <Dialog open={manageCustomersDialogOpen} onOpenChange={setManageCustomersDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex justify-between items-center">
              <DialogTitle className="text-2xl font-bold">View Customers</DialogTitle>
              <Button className="bg-gray-800 hover:bg-gray-900 text-white" onClick={() => { setManageCustomersDialogOpen(false); setAddCustomerDialogOpen(true); }}>
                <Plus size={16} className="mr-2" />Add Customer
              </Button>
            </div>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap justify-between items-center gap-4">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { const tableData = customers.map((c, i) => `${i+1}\t${c.name || ''}\t${c.email || ''}\t${c.phone || ''}\t${c.address || ''}`).join('\n'); navigator.clipboard.writeText(`Sr No\tName\tEmail\tPhone Number\tAddress\n${tableData}`); toast.success('Table copied!'); }}>Copy</Button>
                <Button variant="outline" size="sm" onClick={() => { const headers = 'Sr No,Name,Email,Phone Number,Address'; const rows = customers.map((c, i) => `${i+1},"${c.name || ''}","${c.email || ''}","${c.phone || ''}","${c.address || ''}"`).join('\n'); const blob = new Blob([`${headers}\n${rows}`], { type: 'text/csv' }); const url = window.URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'customers.csv'; a.click(); toast.success('CSV downloaded!'); }}>CSV</Button>
                <Button variant="outline" size="sm" onClick={() => { const headers = 'Sr No\tName\tEmail\tPhone Number\tAddress'; const rows = customers.map((c, i) => `${i+1}\t${c.name || ''}\t${c.email || ''}\t${c.phone || ''}\t${c.address || ''}`).join('\n'); const blob = new Blob([`${headers}\n${rows}`], { type: 'application/vnd.ms-excel' }); const url = window.URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'customers.xls'; a.click(); toast.success('Excel downloaded!'); }}>Excel</Button>
                <Button variant="outline" size="sm" onClick={() => printInvoice()}>Print</Button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Search:</span>
                <Input placeholder="Search customers..." value={customerSearchQuery} onChange={(e) => setCustomerSearchQuery(e.target.value)} className="w-64" />
              </div>
            </div>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Sr No</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Name</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Email</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Phone Number</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Address</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {customers.length === 0 ? (<tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500">No customers found. Click "Add Customer" to create one.</td></tr>) : (
                    customers.filter(customer => { if (!customerSearchQuery) return true; const s = customerSearchQuery.toLowerCase(); return (customer.name || '').toLowerCase().includes(s) || (customer.email || '').toLowerCase().includes(s) || (customer.phone || '').toLowerCase().includes(s) || (customer.address || '').toLowerCase().includes(s); }).map((customer, index) => (
                      <tr key={customer.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">{index + 1}</td>
                        <td className="px-4 py-3 font-medium">{customer.name || 'N/A'}</td>
                        <td className="px-4 py-3">{customer.email || 'N/A'}</td>
                        <td className="px-4 py-3">{customer.phone || 'N/A'}</td>
                        <td className="px-4 py-3">{customer.address || 'N/A'}</td>
                        <td className="px-4 py-3">
                          <Select onValueChange={(action) => { if (action === 'view') toast.info(`Viewing: ${customer.name}`); else if (action === 'edit') { setEditingCustomer(customer); setEditCustomerDialogOpen(true); setManageCustomersDialogOpen(false); } else if (action === 'delete') { if (window.confirm(`Delete "${customer.name}"?`)) axios.delete(`${API}/admin/customers/${customer.id}`).then(() => { toast.success('Deleted!'); fetchCustomers(); }).catch(() => toast.error('Failed')); } }}>
                            <SelectTrigger className="w-20 bg-indigo-100 border-indigo-200"><SelectValue placeholder="..." /></SelectTrigger>
                            <SelectContent><SelectItem value="view">View</SelectItem><SelectItem value="edit">Edit</SelectItem><SelectItem value="delete">Delete</SelectItem></SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="text-sm text-gray-600"><span>Showing {customers.filter(c => { if (!customerSearchQuery) return true; const s = customerSearchQuery.toLowerCase(); return (c.name || '').toLowerCase().includes(s) || (c.email || '').toLowerCase().includes(s) || (c.phone || '').toLowerCase().includes(s) || (c.address || '').toLowerCase().includes(s); }).length} of {customers.length} entries</span></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice View Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-white p-0">
          {selectedInvoice && (
            <div className="invoice-container" id="invoice-print">
              <div className="flex justify-between items-center p-4 border-b">
                <div className="flex items-center gap-3">
                  <img src="/brand-logo.png" alt="Roman Cars Logo" className="h-16" onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
                <div className="text-right"><h2 className="text-2xl font-bold">Booking Invoice</h2></div>
              </div>
              <div className="p-4">
                <div className="bg-yellow-300 px-4 py-2 mb-4 flex justify-between">
                  <p><strong>Customer Name:</strong> {selectedInvoice?.customer_name || 'N/A'}</p>
                  <p><strong>Customer reference number:</strong> {selectedInvoice?.invoiceNumber || 'N/A'}</p>
                </div>
                <div className="grid grid-cols-2 gap-8 mb-4 text-sm">
                  <div className="space-y-1"><p><strong>Supplier Name:</strong> {siteSettings?.logo_text || 'Roman Cars'}{siteSettings?.contact?.address ? ` - ${siteSettings.contact.address}` : ''}</p><p><strong>License number:</strong> LN202300</p></div>
                  <div className="space-y-1 text-right"><p><strong>End of contract:</strong> {selectedInvoice?.customer_name || 'N/A'}</p><p><strong>Invoice No:</strong> {selectedInvoice?.invoiceNumber || 'N/A'}</p></div>
                </div>
                <div className="bg-yellow-300 px-4 py-2 mb-4"><p className="text-sm"><strong>Invoice date:</strong> {(selectedInvoice || {}).booking_date || new Date().toLocaleDateString('en-GB')}</p></div>
                <div className="grid grid-cols-2 gap-8 mb-4 text-sm">
                  <div className="space-y-1"><p><strong>Metro Bank Sort code:</strong> 230580</p><p><strong>AC number:</strong> 31961475</p></div>
                  <div className="space-y-1 text-right"><p><strong>Email:</strong> {(selectedInvoice || {}).customer_email || 'N/A'}</p><p><strong>Address:</strong> {(selectedInvoice || {}).pickup_location?.address || 'N/A'}</p></div>
                </div>
                <div className="bg-yellow-300 px-4 py-2 mb-2"><h3 className="font-bold text-lg">Details</h3></div>
                <div className="mb-4"><div className="overflow-x-auto"><table className="w-full text-sm border border-gray-400"><thead className="bg-gray-200"><tr><th className="border border-gray-400 px-3 py-2 text-left font-bold">Date</th><th className="border border-gray-400 px-3 py-2 text-left font-bold">Time</th><th className="border border-gray-400 px-3 py-2 text-left font-bold">Pickup</th><th className="border border-gray-400 px-3 py-2 text-left font-bold">Drop off</th><th className="border border-gray-400 px-3 py-2 text-right font-bold">Total Fare</th><th className="border border-gray-400 px-3 py-2 text-right font-bold">Subtotal</th></tr></thead><tbody><tr className="hover:bg-gray-50"><td className="border border-gray-400 px-3 py-2">{(selectedInvoice || {}).booking_date || 'N/A'}</td><td className="border border-gray-400 px-3 py-2">{(selectedInvoice || {}).booking_time || '00:00:00'} To {(selectedInvoice || {}).dropoff_time || '00:00:00'}</td><td className="border border-gray-400 px-3 py-2">{(selectedInvoice || {}).pickup_location?.address || 'N/A'}</td><td className="border border-gray-400 px-3 py-2">{(selectedInvoice || {}).dropoff_location?.address || 'N/A'}</td><td className="border border-gray-400 px-3 py-2 text-right">£ {((selectedInvoice || {}).price || 0).toFixed(2)}</td><td className="border border-gray-400 px-3 py-2 text-right">£ {((selectedInvoice || {}).price || 0).toFixed(2)}</td></tr></tbody></table></div></div>
                <div className="bg-yellow-300 px-4 py-2 mb-4 text-right"><p className="text-xl font-bold">Total Payable: £ {((selectedInvoice || {}).price || 0).toFixed(2)}</p></div>
                <div className="mb-4 px-4"><p className="text-sm"><strong>NOTE:</strong> Driver: {(selectedInvoice || {}).driver?.name || 'N/A'} | Vehicle: {(selectedInvoice || {}).vehicle_type || 'N/A'}</p></div>
                <div className="flex gap-4 justify-center mt-6 no-print">
                  <Button variant="outline" onClick={() => setInvoiceDialogOpen(false)}>Close</Button>
                  <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => { const csv = "Date,Time,Pickup,Drop off,Total Fare,Subtotal\n" + selectedInvoice?.booking_date + "," + selectedInvoice?.booking_time + "," + (selectedInvoice?.pickup_location?.address || '') + "," + (selectedInvoice?.dropoff_location?.address || '') + "," + (selectedInvoice?.price || 0).toFixed(2) + "," + (selectedInvoice?.price || 0).toFixed(2); const blob = new Blob([csv], {type: 'application/vnd.ms-excel'}); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'invoice_' + selectedInvoice?.invoiceNumber + '.xls'; a.click(); toast.success('Excel downloaded!'); }}>Download Excel</Button>
                  <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => printInvoice()}>Download PDF</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>



      {/* Driver Report Dialog */}

      

      {/* Ride Report Dialog */}
      


      {/* Contract Invoice Dialog */}
      <Dialog open={contractInvoiceDialogOpen} onOpenChange={setContractInvoiceDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-white p-0">
          {contractInvoiceData && (
            <div className="invoice-container" id="invoice-print">
              <div className="flex justify-between items-center p-4 border-b">
                  <div className="grid grid-cols-2 gap-4 p-3 bg-blue-100 rounded">
                    <div className="space-y-2">
                      <Label>PHV Licence</Label>
                      {editingDriver?.phv_licence && <p className="text-xs text-green-600">Document on file</p>}
                      <Input type="file" accept="image/*,application/pdf" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { setEditingDriver({...editingDriver, phv_licence: reader.result}); }; reader.readAsDataURL(file); } }} className="cursor-pointer" />
                    </div>
                    <div className="space-y-2">
                      <Label>PHV Expiry</Label>
                      <Input type="date" value={editingDriver?.phv_licence_expiry} onChange={(e) => setEditingDriver({...editingDriver, phv_licence_expiry: e.target.value})} />
                    </div>
                  </div>
                <img src="/brand-logo.png" alt="Roman Cars" className="h-20" onError={(e) => e.target.style.display='none'} />
                <h2 className="text-3xl font-bold">Booking Invoice</h2>
              </div>
              <div className="p-6">
                <div className="bg-yellow-400 px-4 py-3 mb-4 flex justify-between">
                  <p><strong>Customer Name :</strong> {contractInvoiceData.customer?.name || contractInvoiceData.contract?.contract_title || 'N/A'}</p>
                  <p><strong>Customer reference number :</strong> {contractInvoiceData.contract?.contract_number || 'N/A'}</p>
                </div>
                <div className="grid grid-cols-2 gap-8 mb-4">
                  <div className="space-y-2 border-r pr-4">
                    <p><strong>Supplier Name :</strong> Roman cars St Albans</p>
                    <p><strong>License number :</strong> LN202300</p>
                  </div>
                  <div className="space-y-2 pl-4">
                    <p><strong>End of contract :</strong> {contractInvoiceData.customer?.name || 'N/A'}</p>
                    <p><strong>Invoice No :</strong> {contractInvoiceData.invoice_no}</p>
                  </div>
                </div>
                <div className="bg-yellow-400 px-4 py-3 mb-4">
                  <p><strong>Invoice date :</strong> {new Date(contractInvoiceData.invoice_date).toLocaleDateString('en-GB')}</p>
                </div>
                <div className="grid grid-cols-2 gap-8 mb-4">
                  <div className="space-y-2 border-r pr-4">
                    <p><strong>Metro Bank Sort code :</strong> 230580</p>
                    <p><strong>AC number :</strong> 31961475</p>
                  </div>
                  <div className="space-y-2 pl-4">
                    <p><strong>Email :</strong> {contractInvoiceData.customer?.email || 'N/A'}</p>
                    <p><strong>Address :</strong> {contractInvoiceData.customer?.address || 'N/A'}</p>
                  </div>
                </div>
                <div className="bg-yellow-400 px-4 py-2 mb-2">
                  <h3 className="font-bold text-lg">Details</h3>
                </div>
                <table className="w-full border border-gray-400 mb-4">
                  <thead className="bg-gray-200">
                    <tr>
                      <th className="border border-gray-400 px-3 py-2 text-left">Date</th>
                      <th className="border border-gray-400 px-3 py-2 text-left">Time</th>
                      <th className="border border-gray-400 px-3 py-2 text-left">Pickup</th>
                      <th className="border border-gray-400 px-3 py-2 text-left">Drop off</th>
                      <th className="border border-gray-400 px-3 py-2 text-right">Total Fare</th>
                      <th className="border border-gray-400 px-3 py-2 text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contractInvoiceData.rides && contractInvoiceData.rides.length > 0 ? contractInvoiceData.rides.map((ride, i) => (
                      <tr key={i}>
                        <td className="border border-gray-400 px-3 py-2">{new Date(ride.pickup_date || ride.booking_date || ride.created_at).toLocaleDateString('en-GB')}</td>
                        <td className="border border-gray-400 px-3 py-2">{ride.booking_time || ride.pickup_time || '00:00'} To {ride.dropoff_time || '00:00'}</td>
                        <td className="border border-gray-400 px-3 py-2">{ride.pickup_address || ride.pickup_location?.address || 'N/A'}</td>
                        <td className="border border-gray-400 px-3 py-2">{ride.dropoff_address || ride.dropoff_location?.address || 'N/A'}</td>
                        <td className="border border-gray-400 px-3 py-2 text-right">£ {(ride.price || 0).toFixed(2)}</td>
                        <td className="border border-gray-400 px-3 py-2 text-right">£ {(ride.price || 0).toFixed(2)}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan="6" className="border border-gray-400 px-3 py-4 text-center text-gray-500">No rides found for selected date range</td></tr>
                    )}
                  </tbody>
                </table>
                <div className="bg-yellow-400 px-4 py-3 mb-4 text-right">
                  <p className="text-xl font-bold">Total Payable : £ {contractInvoiceData.rides ? contractInvoiceData.rides.reduce((sum, r) => sum + (r.price || 0), 0).toFixed(2) : '0.00'}</p>
                </div>
                <div className="mb-4">
                  <p><strong>NOTE:</strong> {contractInvoiceData.invoice_note || 'N/A'}</p>
                </div>
                <div className="flex gap-4 justify-center mt-6">
                  <Button variant="outline" onClick={() => setContractInvoiceDialogOpen(false)}>Back</Button>
                  <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => {
                    const csv = "Date,Time,Pickup,Drop off,Total Fare,Subtotal\n" + (contractInvoiceData.rides || []).map(r => 
                      new Date(r.pickup_date || r.created_at).toLocaleDateString('en-GB') + "," + (r.pickup_time || '') + "," + (r.pickup_address || '') + "," + (r.dropoff_address || '') + "," + (r.price || 0).toFixed(2) + "," + (r.price || 0).toFixed(2)
                    ).join("\n");
                    const blob = new Blob([csv], {type: 'application/vnd.ms-excel'});
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'invoice_' + contractInvoiceData.invoice_no + '.xls';
                    a.click();
                    toast.success('Excel downloaded!');
                  }}>Download Excel</Button>
                  <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => {
                    const rides = (contractInvoiceData.rides || []).sort((a, b) => {
      const dateA = new Date(a.pickup_date || a.booking_date || '1970-01-01');
      const dateB = new Date(b.pickup_date || b.booking_date || '1970-01-01');
      return dateA - dateB;
    });
                    const total = rides.reduce((sum, r) => sum + (r.price || 0), 0).toFixed(2);
                    const ridesHTML = rides.length > 0 ? rides.map(ride => 
                      '<tr><td style="border:1px solid #666;padding:8px;">' + new Date(ride.pickup_date || ride.booking_date || ride.created_at).toLocaleDateString('en-GB') + '</td>' +
                      '<td style="border:1px solid #666;padding:8px;">' + (ride.booking_time || ride.pickup_time || '00:00') + ' To ' + (ride.dropoff_time || '00:00') + '</td>' +
                      '<td style="border:1px solid #666;padding:8px;">' + (ride.pickup_address || (ride.pickup_location && ride.pickup_location.address) || (typeof ride.pickup_location === 'string' ? ride.pickup_location : '') || 'N/A') + '</td>' +
                      '<td style="border:1px solid #666;padding:8px;">' + (ride.dropoff_address || (ride.dropoff_location && ride.dropoff_location.address) || (typeof ride.dropoff_location === 'string' ? ride.dropoff_location : '') || (ride.drop_location && ride.drop_location.address) || (typeof ride.drop_location === 'string' ? ride.drop_location : '') || 'N/A') + '</td>' +
                      '<td style="border:1px solid #666;padding:8px;text-align:right;">£ ' + (ride.price || 0).toFixed(2) + '</td>' +
                      '<td style="border:1px solid #666;padding:8px;text-align:right;">£ ' + (ride.price || 0).toFixed(2) + '</td></tr>'
                    ).join('') : '<tr><td colspan="6" style="border:1px solid #666;padding:20px;text-align:center;">No rides found</td></tr>';
                    const html = '<!DOCTYPE html><html><head><title>Invoice</title></head><body style="font-family:Arial,sans-serif;padding:20px;max-width:900px;margin:0 auto;">' +
                      '<div style="display:flex;justify-content:space-between;align-items:center;padding:15px;border-bottom:2px solid #000;margin-bottom:20px;"><img src="/brand-logo.png" style="height:80px;" /><h2 style="font-size:28px;margin:0;">Booking Invoice</h2></div>' +
                      '<div style="background:#FDE047;padding:12px 16px;margin-bottom:15px;display:flex;justify-content:space-between;"><span><strong>Customer Name :</strong> ' + (contractInvoiceData.customer?.name || contractInvoiceData.contract?.contract_title || 'N/A') + '</span><span><strong>Customer reference number :</strong> ' + (contractInvoiceData.contract?.contract_number || 'N/A') + '</span></div>' +
                      '<div style="display:flex;margin-bottom:15px;"><div style="flex:1;border-right:1px solid #ccc;padding-right:20px;"><p><strong>Supplier Name :</strong> Roman cars St Albans</p><p><strong>License number :</strong> LN202300</p></div><div style="flex:1;padding-left:20px;"><p><strong>End of contract :</strong> ' + (contractInvoiceData.customer?.name || 'N/A') + '</p><p><strong>Invoice No :</strong> ' + contractInvoiceData.invoice_no + '</p></div></div>' +
                      '<div style="background:#FDE047;padding:12px 16px;margin-bottom:15px;"><strong>Invoice date :</strong> ' + new Date(contractInvoiceData.invoice_date).toLocaleDateString('en-GB') + '</div>' +
                      '<div style="display:flex;margin-bottom:15px;"><div style="flex:1;border-right:1px solid #ccc;padding-right:20px;"><p><strong>Metro Bank Sort code :</strong> 230580</p><p><strong>AC number :</strong> 31961475</p></div><div style="flex:1;padding-left:20px;"><p><strong>Email :</strong> ' + (contractInvoiceData.customer?.email || 'N/A') + '</p><p><strong>Address :</strong> ' + (contractInvoiceData.customer?.address || 'N/A') + '</p></div></div>' +
                      '<div style="background:#FDE047;padding:10px 16px;margin-bottom:10px;"><strong>Details</strong></div>' +
                      '<table style="width:100%;border-collapse:collapse;margin-bottom:15px;"><thead style="background:#E5E7EB;"><tr><th style="border:1px solid #666;padding:8px;text-align:left;">Date</th><th style="border:1px solid #666;padding:8px;text-align:left;">Time</th><th style="border:1px solid #666;padding:8px;text-align:left;">Pickup</th><th style="border:1px solid #666;padding:8px;text-align:left;">Drop off</th><th style="border:1px solid #666;padding:8px;text-align:right;">Total Fare</th><th style="border:1px solid #666;padding:8px;text-align:right;">Subtotal</th></tr></thead><tbody>' + ridesHTML + '</tbody></table>' +
                      '<div style="background:#FDE047;padding:15px 16px;margin-bottom:15px;text-align:right;"><strong style="font-size:20px;">Total Payable : £ ' + total + '</strong></div>' +
                      '<div style="margin-bottom:20px;"><p><strong>NOTE:</strong> ' + (contractInvoiceData.invoice_note || 'N/A') + '</p></div></body></html>';
                    const printWindow = window.open('', '_blank');
                    printWindow.document.write(html);
                    printWindow.document.close();
                    printWindow.focus();
                    setTimeout(() => { printWindow.print(); }, 500);
                  }}>Download PDF</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Contract Dialog */}
      <Dialog open={editContractDialogOpen} onOpenChange={setEditContractDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Contract</DialogTitle>
          </DialogHeader>
          {editingContract && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contract Title</Label>
                  <Input value={editingContract.contract_title || ''} onChange={(e) => setEditingContract({...editingContract, contract_title: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input value={editingContract.company_name || ''} onChange={(e) => setEditingContract({...editingContract, company_name: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact Person</Label>
                  <Input value={editingContract.contact_person || ''} onChange={(e) => setEditingContract({...editingContract, contact_person: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={editingContract.phone || ''} onChange={(e) => setEditingContract({...editingContract, phone: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={editingContract.email || ''} onChange={(e) => setEditingContract({...editingContract, email: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input value={editingContract.address || ''} onChange={(e) => setEditingContract({...editingContract, address: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Service Type</Label>
                  <Select value={editingContract.service_type || 'airport_transfer'} onValueChange={(value) => setEditingContract({...editingContract, service_type: value})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="airport_transfer">Airport Transfer</SelectItem>
                      <SelectItem value="regular_transport">Regular Transport</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Car Type</Label>
                  <Select value={editingContract.car_type || 'saloon'} onValueChange={(value) => setEditingContract({...editingContract, car_type: value})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="saloon">Saloon</SelectItem>
                      <SelectItem value="estate">Estate</SelectItem>
                      <SelectItem value="mpv">MPV</SelectItem>
                      <SelectItem value="luxury">Luxury</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pickup Location</Label>
                  <Input value={editingContract.pickup_location || ''} onChange={(e) => setEditingContract({...editingContract, pickup_location: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Drop Location</Label>
                  <Input value={editingContract.drop_location || ''} onChange={(e) => setEditingContract({...editingContract, drop_location: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pickup Time</Label>
                  <Input type="time" value={editingContract.pickup_time || ''} onChange={(e) => setEditingContract({...editingContract, pickup_time: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Drop Time</Label>
                  <Input type="time" value={editingContract.drop_time || ''} onChange={(e) => setEditingContract({...editingContract, drop_time: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Total Fare (£)</Label>
                  <Input type="number" value={editingContract.total_fare || ''} onChange={(e) => setEditingContract({...editingContract, total_fare: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Total Miles</Label>
                  <Input type="number" value={editingContract.total_miles || ''} onChange={(e) => setEditingContract({...editingContract, total_miles: e.target.value})} />
                </div>
              </div>
            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold mb-4">Payment Details</h3>
              <div className="space-y-2 mb-4">
                <Label>Driver Payment Type *</Label>
                <Select value={editingContract.payment_type || "flat"} onValueChange={(v) => setEditingContract({...editingContract, payment_type: v})}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flat">Flat Amount</SelectItem>
                    <SelectItem value="percentage">Percentage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{editingContract.payment_type === "percentage" ? "Driver Payable (%)" : "Driver Payable (£)"} *</Label>
                  <Input type="number" step="0.01" min="0" value={editingContract.driver_payable || ""} onChange={(e) => setEditingContract({...editingContract, driver_payable: parseFloat(e.target.value) || 0})} />
                </div>
                <div className="space-y-2">
                  <Label>Roman Cars Payment (£) *</Label>
                  <Input type="number" step="0.01" min="0" value={editingContract.roman_cars_payment || ""} onChange={(e) => setEditingContract({...editingContract, roman_cars_payment: parseFloat(e.target.value) || 0})} />
                </div>
              </div>
            </div>
              <div className="flex gap-2 pt-4">
                <Button onClick={() => {
                  axios.put(`${API}/admin/contracts/${editingContract.id}`, editingContract)
                    .then(() => {
                      toast.success('Contract updated!');
                      setEditContractDialogOpen(false);
                      setEditingContract(null);
                      fetchContracts();
                    })
                    .catch(() => toast.error('Failed to update contract'));
                }} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
                  Save Changes
                </Button>
                <Button variant="outline" onClick={() => setEditContractDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Contract Dialog */}
      <Dialog open={viewContractDialogOpen} onOpenChange={setViewContractDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Contract Details</DialogTitle>
          </DialogHeader>
          {viewingContract && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><strong>Contract Title:</strong> {viewingContract.contract_title || '-'}</div>
                <div><strong>Company Name:</strong> {viewingContract.company_name || '-'}</div>
                <div><strong>Contact Person:</strong> {viewingContract.contact_person || '-'}</div>
                <div><strong>Phone:</strong> {viewingContract.phone || '-'}</div>
                <div><strong>Email:</strong> {viewingContract.email || '-'}</div>
                <div><strong>Address:</strong> {viewingContract.address || '-'}</div>
                <div><strong>Service Type:</strong> {viewingContract.service_type === 'airport_transfer' ? 'Airport Transfer' : 'Regular Transport'}</div>
                <div><strong>Car Type:</strong> {viewingContract.car_type || '-'}</div>
                <div><strong>Pickup Location:</strong> {viewingContract.pickup_location || '-'}</div>
                <div><strong>Drop Location:</strong> {viewingContract.drop_location || '-'}</div>
                <div><strong>Pickup Time:</strong> {viewingContract.pickup_time || '-'}</div>
                <div><strong>Drop Time:</strong> {viewingContract.drop_time || '-'}</div>
                <div><strong>Total Fare:</strong> £{viewingContract.total_fare || '0'}</div>
                <div><strong>Total Miles:</strong> {viewingContract.total_miles || '0'}</div>
                <div><strong>Status:</strong> {viewingContract.status || 'active'}</div>
              </div>
              <Button variant="outline" onClick={() => setViewContractDialogOpen(false)} className="w-full mt-4">
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
